import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../components/auth/AuthContext';
import { api } from '../../services/api';
import * as Sentry from "@sentry/react";

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'boot-source-selector' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'boot-source-selector' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'boot-source-selector',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// Security: Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

// Security: Safe error messages mapping
const ERROR_MESSAGES = {
  'AUTH_ERROR': 'Please log in again to continue',
  'VOLUME_LIST_FAILED': 'Unable to load volumes. Please refresh the page.',
  'CUSTOM_IMAGE_LIST_FAILED': 'Unable to load custom images. Please try again.',
  'OS_IMAGE_LIST_FAILED': 'Unable to load OS images. Please try again.',
  'REGION_DATA_FAILED': 'Unable to load data for selected region.',
  'VALIDATION_ERROR': 'Please check your selection and try again',
  'SERVER_ERROR': 'Service temporarily unavailable',
  'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  'INVALID_REQUEST': 'Invalid request format',
  'NETWORK_ERROR': 'Network connection failed. Please check your connection.',
  'PERMISSION_ERROR': 'You do not have permission to access this resource',
  'VOLUME_ATTACHED': 'Boot volume is currently attached to another VM. Please detach it first or select a different volume.',
  'OFFLINE': "You're offline. Check your connection.",
  'TIMEOUT': "Request timed out. Please try again.",
  'SERVER_UNREACHABLE': "Network connection failed. Please check your internet connection and try again.",
  'NO_RESPONSE': "No response from server. Please try again.",
  'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again.",
  'TOO_MANY_REQUESTS': "Too many requests. Please wait before trying again."
};

const getSafeErrorMessage = (err) => {
  if (!err || typeof err !== 'object') {
    return 'Something went wrong. Please try again.';
  }
  
  // Known error code from mapping
  if (err.code && ERROR_MESSAGES[err.code]) {
    return ERROR_MESSAGES[err.code];
  }
  
  // Interceptor's user-friendly message
  if (err.error) {
    return err.error;
  }
  
  return 'Something went wrong. Please try again.';
};
// Security: Validate error object structure
const validateErrorObject = (error) => {
  if (!error || typeof error !== 'object') return false;
  return true;
};


// Network error detection with 6-position priority
const getNetworkErrorMessage = (error) => {
  if (!navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return {
      message: "Request timed out. Please try again.",
      code: 'TIMEOUT'
    };
  }
  
  if (error.code === 'ERR_NETWORK' && navigator.onLine) {
    return {
      message: "Unable to reach server. Please try later.",
      code: 'SERVER_UNREACHABLE'
    };
  }
  
  if (error.code === 'ERR_NETWORK' && !navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  if (error.request && !error.response) {
    return {
      message: "No response from server. Please try again.",
      code: 'NO_RESPONSE'
    };
  }
  
  return {
    message: "Something went wrong. Please try again.",
    code: 'UNKNOWN_NETWORK_ERROR'
  };
};
const validateVolumeName = (name, existingVolumes = []) => {
  const trimmedName = name.trim();
  
  if (trimmedName.length < 3) {
    return { valid: false, error: 'Volume name must be at least 3 characters' };
  }
  
  if (trimmedName.length > 30) {
    return { valid: false, error: 'Volume name cannot exceed 30 characters' };
  }
  
  const validPattern = /^[a-zA-Z0-9_-]+$/;
  if (!validPattern.test(trimmedName)) {
    return { valid: false, error: 'Volume name can only contain letters, numbers, hyphens, and underscores' };
  }
  
  if (trimmedName.startsWith('-') || trimmedName.startsWith('_')) {
    return { valid: false, error: 'Volume name cannot start with a hyphen or underscore' };
  }
  
  if (trimmedName.endsWith('-') || trimmedName.endsWith('_')) {
    return { valid: false, error: 'Volume name cannot end with a hyphen or underscore' };
  }
  
  const nameLower = trimmedName.toLowerCase();
  const isDuplicate = existingVolumes.some(vol => vol.name.toLowerCase() === nameLower);
  if (isDuplicate) {
    return { valid: false, error: 'A volume with this name already exists' };
  }
  
  return { valid: true, error: '' };
};

const BootSourceSelector = ({ 
  // Volume name validation function - matches backend requirements
  sources = [], 
  selectedSource = '', 
  onSelectSource, 
  osVersions = [], 
  selectedOS = '', 
  onSelectOS, 
  selectedRegion = '',
  volumes = [],
  selectedVolume = '',
  onSelectVolume,
  onNavigateToVolumes = () => {},
  customImages = [],
  selectedCustomImage = '',
  onSelectCustomImage = () => {},
  onOsVolumeSizeChange = () => {},
  onNewVolumesChange = () => {},
  onAttachableVolumesChange = () => {}
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredVersions, setFilteredVersions] = useState([]);
  const [bootableVolumes, setBootableVolumes] = useState([]);
  const [nonBootableVolumes, setNonBootableVolumes] = useState([]);
  const [filteredCustomImages, setFilteredCustomImages] = useState([]);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);
  

  // Advanced region specific states
  const [attachableVolumes, setAttachableVolumes] = useState([]);
  const [selectedAttachableVolumes, setSelectedAttachableVolumes] = useState([]);
  const [newVolumes, setNewVolumes] = useState([]);
  const [showVolumeModal, setShowVolumeModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAttachModal, setShowAttachModal] = useState(false);
  
  const [attachModalSearch, setAttachModalSearch] = useState('');
  const [editingVolume, setEditingVolume] = useState(null);
  const [osVolumeSize, setOsVolumeSize] = useState(100);
  const [inputFocused, setInputFocused] = useState(false);
  const [inputValue, setInputValue] = useState('100 GB');
  const [nameError, setNameError] = useState('');
  const [isInputValid, setIsInputValid] = useState(true);// Add below existing useState declarations
const [selectedImageType, setSelectedImageType] = useState('public'); // 'public' or 'user_volume'
const [selectedVolumeData, setSelectedVolumeData] = useState(null);
const [showRetryAttachable, setShowRetryAttachable] = useState(false);

  // Loading states for different operations
  const [loadingStates, setLoadingStates] = useState({
    volumes: false,
    customImages: false,
    osImages: false,
    filtering: false,
    attachableVolumes: false
  });

  // Use hooks
const { isAuthenticated, handleAuthError } = useAuth();

  // Region detection - Advanced vs Legacy
const isAdvancedRegion = useMemo(() => {
  return selectedRegion?.includes('finland');
}, [selectedRegion]);

  // Filter attachable volumes to exclude selected OS volume
  const filteredAttachableVolumes = useMemo(() => {
    if (!isAdvancedRegion || selectedSource !== 'attachable') return attachableVolumes;
    
    return attachableVolumes.filter(volume => {
      // Exclude if this volume is selected as OS boot source
      if (selectedImageType === 'user_volume' && selectedVolumeData?.volume_id === volume.id) {
        return false;
      }
      return true;
    });
  }, [attachableVolumes, selectedImageType, selectedVolumeData, selectedSource, isAdvancedRegion]);
  // Set Sentry context when component state changes
  useEffect(() => {
    Sentry.setContext("boot-source-selector", {
      isAuthenticated,
      selectedSource,
      selectedRegion,
      isAdvancedRegion,
      selectedOS,
      selectedVolume,
      selectedCustomImage,
      sourcesCount: sources.length,
      osVersionsCount: osVersions.length,
      volumesCount: volumes.length,
      customImagesCount: customImages.length,
      filteredVersionsCount: filteredVersions.length,
      bootableVolumesCount: bootableVolumes.length,
      filteredCustomImagesCount: filteredCustomImages.length,
      attachableVolumesCount: attachableVolumes.length,
      selectedAttachableVolumesCount: selectedAttachableVolumes.length,
      newVolumesCount: newVolumes.length,
      hasError: !!error,
      isDropdownOpen: isOpen
    });
  }, [
    isAuthenticated, selectedSource, selectedRegion, isAdvancedRegion, selectedOS, selectedVolume, selectedCustomImage,
    sources.length, osVersions.length, volumes.length, customImages.length,
    filteredVersions.length, bootableVolumes.length, filteredCustomImages.length,
    attachableVolumes.length, selectedAttachableVolumes.length, newVolumes.length,
    error, isOpen
  ]);

  // Log component mount
  useEffect(() => {
    logInfo('BootSourceSelector component mounted', {
      isAuthenticated,
      sourcesProvided: sources.length,
      osVersionsProvided: osVersions.length,
      volumesProvided: volumes.length,
      customImagesProvided: customImages.length,
      selectedRegion,
      isAdvancedRegion
    });
    
    logUserAction('boot_source_selector_mounted', {
      isAuthenticated,
      availableSourceTypes: sources.map(s => s.id),
      selectedRegion,
      regionType: isAdvancedRegion ? 'advanced' : 'legacy'
    });
  }, []);


  // Report OS volume size changes to parent
  useEffect(() => {
    if (typeof onOsVolumeSizeChange === 'function') {
      onOsVolumeSizeChange(osVolumeSize);
    }
  }, [osVolumeSize, onOsVolumeSizeChange]);

  // Report new volumes to parent
  useEffect(() => {
    if (typeof onNewVolumesChange === 'function') {
      onNewVolumesChange(newVolumes);
    }
  }, [newVolumes, onNewVolumesChange]);

  // Report attachable volumes to parent
  useEffect(() => {
    if (typeof onAttachableVolumesChange === 'function') {
      onAttachableVolumesChange(selectedAttachableVolumes);
    }
  }, [selectedAttachableVolumes, onAttachableVolumesChange]);

  // Filter sources based on authentication and region type
  const availableSources = useMemo(() => {
    const startTime = performance.now();
    
    if (!Array.isArray(sources)) {
      logError('Invalid sources array provided', {
        sourcesType: typeof sources,
        sourcesValue: sources
      });
      return [];
    }
    
    let filtered;
    // For unauthenticated users, only show OS Images
    if (!isAuthenticated) {
      filtered = sources.filter(source => source.id === 'os_images');
      
      logInfo('Filtered sources for unauthenticated user', {
        originalCount: sources.length,
        filteredCount: filtered.length,
        allowedTypes: ['os_images']
      });
    } else {

      // For authenticated users, filter by region type
      if (isAdvancedRegion) {
        // Advanced regions: OS Images, Volumes, Attach Volumes
        filtered = [
          { id: 'os_images', name: 'OS Images' },
          { id: 'volumes', name: 'Volumes' },
          { id: 'attachable', name: 'Attach Volumes' }
        ];
        
        logInfo('Advanced region sources filtered', {
          originalCount: sources.length,
          filteredCount: filtered.length,
          allowedTypes: ['os_images', 'volumes', 'attachable']
        });
      } else {
        // Legacy regions: OS Images, Volumes, Custom Images (current behavior)
        filtered = sources;
        
        logInfo('Legacy region - all sources available', {
          sourcesCount: sources.length,
          sourceTypes: sources.map(s => s.id)
        });
      }
    }
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    logPerformance('filter_available_sources', duration, {
      isAuthenticated,
      isAdvancedRegion,
      originalCount: sources.length,
      filteredCount: filtered.length,
      sourceTypes: filtered.map(s => s.id)
    });
    
    return filtered;
  }, [sources, isAuthenticated, isAdvancedRegion]);
  // Auto-select first available source when available sources change
useEffect(() => {
  if (availableSources.length > 0) {
    const currentSourceExists = availableSources.some(source => source.id === selectedSource);
    if (!currentSourceExists) {
      // Current selection not available, auto-select first available
      const firstSource = availableSources[0].id;
      onSelectSource(firstSource);
      
      logUserAction('boot_source_auto_selected', {
        previousSource: selectedSource,
        newSource: firstSource,
        reason: 'source_not_available_in_region',
        availableSources: availableSources.map(s => s.id),
        isAdvancedRegion
      });
    }
  }
}, [availableSources, selectedSource, onSelectSource, isAdvancedRegion]);

  // Enhanced error processing function
  const handleOperationError = (error, operation) => {
    const safeMessage = getSafeErrorMessage(error);
    setError(safeMessage);
    
    logError(`Operation failed: ${operation}`, {
      operation,
      errorMessage: error?.message,
      errorName: error?.name,
      safeMessage,
      selectedSource,
      selectedRegion,
      isAdvancedRegion
    }, error);
  };

  // Clear error state
  const clearError = () => {
    if (error) {
      logInfo('Error cleared', {
        previousError: error,
        selectedSource,
        selectedRegion,
        isAdvancedRegion
      });
    }
    setError(null);
  };

const fetchAttachableVolumes = async () => {
  if (!isAdvancedRegion || selectedSource !== 'attachable') return;
  
  try {
    setLoadingStates(prev => ({ ...prev, attachableVolumes: true }));
    clearError();
    
    logInfo('Fetching attachable volumes', {
      selectedRegion,
      isAdvancedRegion
    });
    
    const response = await api.getVolumes({ 
      region: selectedRegion, 
      status: 'detached',
      exclude_os_volumes: false
    });
    
    if (response.success && response.volumes) {
      setAttachableVolumes(response.volumes);
      setShowRetryAttachable(false);
      
      logInfo('Attachable volumes fetched successfully', {
        count: response.volumes.length,
        selectedRegion
      });
    } else {
      throw new Error('Failed to fetch attachable volumes');
    }
  } catch (err) {
    // Position 1: Authentication error
    if (err.status === 401 || err.code === 'SESSION_EXPIRED' || err.code === 'AUTH_ERROR') {
      handleAuthError();
      return;
    }
    
    // Position 2: Network errors (interceptor sets code, no status)
    const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
    if (networkErrorCodes.includes(err.code)) {
      setError(err.error);
      setShowRetryAttachable(true);
      logError('Network error in fetchAttachableVolumes', { 
        code: err.code, 
        message: err.error,
        online: navigator.onLine,
        selectedRegion
      }, err);
      setAttachableVolumes([]);
      return;
    }
    
    // Position 3: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      setError(err.error || 'Too many requests. Please wait before trying again.');
      setShowRetryAttachable(true);
      setAttachableVolumes([]);
      return;
    }
    
    // Position 4: Known error codes
    if (err.code && ERROR_MESSAGES[err.code]) {
      setError(ERROR_MESSAGES[err.code]);
      setShowRetryAttachable(true);
      logError('Attachable volumes fetch failed', {
        code: err.code,
        selectedRegion
      }, err);
      setAttachableVolumes([]);
      return;
    }
    
    // Position 5: Server errors
    if (err.status >= 500 || err.code === 'SERVER_ERROR' || err.code === 'SERVICE_UNAVAILABLE') {
      setError(err.error || 'Service temporarily unavailable. Please try again later.');
      setShowRetryAttachable(true);
      setAttachableVolumes([]);
      return;
    }
    
    // Position 6: Fallback
    handleOperationError(err, 'attachableVolumes');
    setAttachableVolumes([]);
  } finally {
    setLoadingStates(prev => ({ ...prev, attachableVolumes: false }));
  }
};  // Fetch attachable volumes when needed
  useEffect(() => {
    fetchAttachableVolumes();
  }, [isAdvancedRegion, selectedSource, selectedRegion]);

  // Retry function for attachable volumes fetch
const retryFetchAttachableVolumes = useCallback(() => {
  setError(null);
  setShowRetryAttachable(false);
  fetchAttachableVolumes();
}, [fetchAttachableVolumes]);
// Sanitize and filter OS versions by region when dependency changes
useEffect(() => {
  const filterOSVersions = async () => {
    const startTime = performance.now();
    
    try {
      setLoadingStates(prev => ({ ...prev, osImages: true, filtering: true }));
      clearError();
     
      logInfo('Starting OS versions filtering', {
        totalOsVersions: osVersions.length,
        selectedRegion,
        selectedSource,
        isAdvancedRegion
      });
     
      if (!Array.isArray(osVersions)) {
        setFilteredVersions([]);
        logError('Invalid osVersions array', {
          osVersionsType: typeof osVersions,
          osVersionsValue: osVersions
        });
        return;
      }

      // Simulate processing time for large datasets
      if (osVersions.length > 100) {
        await new Promise(resolve => setTimeout(resolve, 100));
        logInfo('Applied processing delay for large OS dataset', {
          osVersionsCount: osVersions.length
        });
      }

      const filtered = osVersions.filter(image => {
        return image && image.id && image.name;
      });

      setFilteredVersions(filtered);
     
      const endTime = performance.now();
      const duration = endTime - startTime;
     
      logPerformance('filter_os_versions', duration, {
        originalCount: osVersions.length,
        filteredCount: filtered.length,
        selectedRegion,
        hasSelectedRegion: !!selectedRegion,
        isAdvancedRegion
      });
     
      logInfo('OS versions filtering completed', {
        originalCount: osVersions.length,
        filteredCount: filtered.length,
        selectedRegion,
        duration: `${duration.toFixed(2)}ms`,
        isAdvancedRegion
      });
     
      // If current OS selection is not in filtered list, select first available one
      if (filtered.length > 0 && selectedOS && selectedSource === 'os_images') {
        const osExists = filtered.some(os => os.id === selectedOS);
        if (!osExists && onSelectOS) {
          const newSelection = filtered[0].id;
          onSelectOS(newSelection);
         
          logUserAction('os_auto_selected_after_filter', {
            previousSelection: selectedOS,
            newSelection,
            filteredCount: filtered.length,
            reason: 'previous_selection_not_available_in_region',
            isAdvancedRegion
          });
         
          logInfo('Auto-selected first OS after filtering', {
            previousSelection: selectedOS,
            newSelection,
            selectedRegion,
            isAdvancedRegion
          });
        }
      }
     
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
     
      handleOperationError(error, 'osImages');
      setFilteredVersions([]);
     
      logError('OS versions filtering failed', {
        selectedRegion,
        osVersionsCount: osVersions.length,
        duration: `${duration.toFixed(2)}ms`,
        isAdvancedRegion
      }, error);
    } finally {
      setLoadingStates(prev => ({ ...prev, osImages: false, filtering: false }));
    }
  };

  filterOSVersions();
}, [osVersions, selectedRegion, selectedOS, onSelectOS, selectedSource, isAdvancedRegion]);

  // Filter custom images by region with error handling (LEGACY REGIONS ONLY)
  useEffect(() => {
    if (isAdvancedRegion) return; // Skip for advanced regions
    
    const filterCustomImages = async () => {
      if (selectedSource !== 'custom_images') return;
      
      const startTime = performance.now();
      
      try {
        setLoadingStates(prev => ({ ...prev, customImages: true }));
        clearError();
        
        logInfo('Starting custom images filtering', {
          totalCustomImages: customImages.length,
          selectedRegion,
          selectedSource,
          isAdvancedRegion
        });
        
        if (!Array.isArray(customImages)) {
          setFilteredCustomImages([]);
          logError('Invalid customImages array', {
            customImagesType: typeof customImages,
            customImagesValue: customImages
          });
          return;
        }

        // Simulate processing for large datasets
        if (customImages.length > 50) {
          await new Promise(resolve => setTimeout(resolve, 150));
          logInfo('Applied processing delay for large custom images dataset', {
            customImagesCount: customImages.length
          });
        }

        // Filter by selected region
        const filtered = selectedRegion
          ? customImages.filter(image => {
              if (!image || typeof image !== 'object') return false;
              
              // Match by region_name
              const imageRegion = image.region_name || '';
              return imageRegion.toLowerCase() === selectedRegion.toLowerCase() || 
                     imageRegion.replace(/\s+/g, '-').toLowerCase() === selectedRegion.toLowerCase();
            })
          : customImages;

        setFilteredCustomImages(filtered);

        const endTime = performance.now();
        const duration = endTime - startTime;
        
        logPerformance('filter_custom_images', duration, {
          originalCount: customImages.length,
          filteredCount: filtered.length,
          selectedRegion,
          hasSelectedRegion: !!selectedRegion,
          isAdvancedRegion
        });
        
        logInfo('Custom images filtering completed', {
          originalCount: customImages.length,
          filteredCount: filtered.length,
          selectedRegion,
          duration: `${duration.toFixed(2)}ms`,
          isAdvancedRegion
        });

        // If current custom image selection is not in filtered list, select first available one
        if (filtered.length > 0 && selectedCustomImage && selectedSource === 'custom_images') {
          const imageExists = filtered.some(img => img.id === selectedCustomImage);
          if (!imageExists && onSelectCustomImage) {
            const newSelection = filtered[0].id;
            onSelectCustomImage(newSelection);
            
            logUserAction('custom_image_auto_selected_after_filter', {
              previousSelection: selectedCustomImage,
              newSelection,
              filteredCount: filtered.length,
              reason: 'previous_selection_not_available_in_region',
              isAdvancedRegion
            });
            
            logInfo('Auto-selected first custom image after filtering', {
              previousSelection: selectedCustomImage,
              newSelection,
              selectedRegion,
              isAdvancedRegion
            });
          }
        }
        
      } catch (error) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        handleOperationError(error, 'customImages');
        setFilteredCustomImages([]);
        
        logError('Custom images filtering failed', {
          selectedRegion,
          customImagesCount: customImages.length,
          duration: `${duration.toFixed(2)}ms`,
          isAdvancedRegion
        }, error);
      } finally {
        setLoadingStates(prev => ({ ...prev, customImages: false }));
      }
    };

    filterCustomImages();
  }, [customImages, selectedRegion, selectedCustomImage, onSelectCustomImage, selectedSource, isAdvancedRegion]);

  // Filter volumes by region and separate bootable from non-bootable with error handling
  useEffect(() => {
    const filterVolumes = async () => {
      if (selectedSource !== 'volumes') return;
      
      const startTime = performance.now();
      
      try {
        setLoadingStates(prev => ({ ...prev, volumes: true }));
        clearError();
        
        logInfo('Starting volumes filtering', {
          totalVolumes: volumes.length,
          selectedRegion,
          selectedSource,
          isAdvancedRegion
        });
        
        if (!Array.isArray(volumes)) {
          setBootableVolumes([]);
          setNonBootableVolumes([]);
          logError('Invalid volumes array', {
            volumesType: typeof volumes,
            volumesValue: volumes
          });
          return;
        }

        // Simulate processing for large volume lists
        if (volumes.length > 100) {
          await new Promise(resolve => setTimeout(resolve, 200));
          logInfo('Applied processing delay for large volumes dataset', {
            volumesCount: volumes.length
          });
        }

        // Filter by selected region
        const regionVolumes = selectedRegion
          ? volumes.filter(volume => {
              if (!volume || typeof volume !== 'object') return false;
              const volumeRegion = volume.region || '';
              return volumeRegion.toLowerCase() === selectedRegion.toLowerCase() ||
                     volumeRegion.replace(/\s+/g, '-').toLowerCase() === selectedRegion.toLowerCase();
            })
          : volumes;

        // Separate bootable from non-bootable volumes
        const bootable = regionVolumes.filter(volume => volume.bootable === true);
        const nonBootable = regionVolumes.filter(volume => volume.bootable !== true);

        setBootableVolumes(bootable);
        setNonBootableVolumes(nonBootable);

        const endTime = performance.now();
        const duration = endTime - startTime;
        
        logPerformance('filter_volumes', duration, {
          originalCount: volumes.length,
          regionFilteredCount: regionVolumes.length,
          bootableCount: bootable.length,
          nonBootableCount: nonBootable.length,
          selectedRegion,
          hasSelectedRegion: !!selectedRegion,
          isAdvancedRegion
        });
        
        logInfo('Volumes filtering completed', {
          originalCount: volumes.length,
          regionFilteredCount: regionVolumes.length,
          bootableCount: bootable.length,
          nonBootableCount: nonBootable.length,
          selectedRegion,
          duration: `${duration.toFixed(2)}ms`,
          isAdvancedRegion
        });

        // If a volume was selected but is no longer available in this region, deselect it
        if (selectedVolume && selectedSource === 'volumes') {
          const volumeExists = bootable.some(vol => vol.id === selectedVolume);
          if (!volumeExists && onSelectVolume) {
            onSelectVolume(null);
            
            logUserAction('volume_deselected_after_filter', {
              previousSelection: selectedVolume,
              reason: 'not_available_in_region',
              selectedRegion,
              availableBootableVolumes: bootable.length,
              isAdvancedRegion
            });
            
            logInfo('Deselected volume after filtering', {
              previousSelection: selectedVolume,
              selectedRegion,
              availableBootableVolumes: bootable.length,
              isAdvancedRegion
            });
          }
        }
        
      } catch (error) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        handleOperationError(error, 'volumes');
        setBootableVolumes([]);
        setNonBootableVolumes([]);
        
        logError('Volumes filtering failed', {
          selectedRegion,
          volumesCount: volumes.length,
          duration: `${duration.toFixed(2)}ms`,
          isAdvancedRegion
        }, error);
      } finally {
        setLoadingStates(prev => ({ ...prev, volumes: false }));
      }
    };

    filterVolumes();
  }, [volumes, selectedRegion, selectedVolume, onSelectVolume, selectedSource, isAdvancedRegion]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (isOpen) {
          setIsOpen(false);
          
          logUserAction('dropdown_closed_outside_click', {
            selectedSource,
            wasOpen: isOpen,
            isAdvancedRegion
          });
        }
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef, isOpen, selectedSource, isAdvancedRegion]);
  // Handle ESC key for modals
useEffect(() => {
  const handleEscKey = (event) => {
    if (event.key === 'Escape') {
      if (showVolumeModal) {
        setShowVolumeModal(false);
      } else if (showManageModal) {
        setShowManageModal(false);
      } else if (showEditModal) {
        setShowEditModal(false);
        setEditingVolume(null);
      } else if (showAttachModal) {
        setShowAttachModal(false);
        setAttachModalSearch('');
      }
    }
  };
  
  document.addEventListener('keydown', handleEscKey);
  return () => {
    document.removeEventListener('keydown', handleEscKey);
  };
}, [showVolumeModal, showManageModal, showEditModal, showAttachModal]);

  // Handle OS selection with validation and error handling
const handleSelect = (id) => {
  const previousSelection = selectedOS;
 
  try {
    if (id && typeof onSelectOS === 'function') {
      const selectedItem = filteredVersions.find(item => item.id === id);

     
      if (selectedItem?.is_user_volume) {
        // User OS volume selected
        setSelectedImageType('user_volume');
        setSelectedVolumeData(selectedItem);
        setOsVolumeSize(selectedItem.volume_size || 100);
        setInputValue(`${selectedItem.volume_size || 100} GB`);
      } else {
        // Public image selected
        setSelectedImageType('public');
        setSelectedVolumeData(null);
      }
      
      // Add this after state changes
      setTimeout(() => {
    
      }, 100);
      
      onSelectOS(id);
      setIsOpen(false);
      clearError();
      
      logUserAction('os_selected', {
        previousSelection,
        newSelection: id,
        itemName: selectedItem?.name,
        isUserVolume: selectedItem?.is_user_volume || false,
        selectedRegion,
        availableOptions: filteredVersions.length,
        isAdvancedRegion
      });
    }
  } catch (error) {
    handleOperationError(error, 'osImages');
  }
};

  // Handle custom image selection with error handling (LEGACY REGIONS ONLY)
  const handleCustomImageSelect = (id) => {
    const previousSelection = selectedCustomImage;
    
    try {
      if (id && typeof onSelectCustomImage === 'function') {
        onSelectCustomImage(id);
        setIsOpen(false);
        clearError();
        
        const selectedImageData = filteredCustomImages.find(img => img.id === id);
        
        logUserAction('custom_image_selected', {
          previousSelection,
          newSelection: id,
          imageName: selectedImageData?.name,
          selectedRegion,
          availableOptions: filteredCustomImages.length,
          isAdvancedRegion
        });
        
        logInfo('Custom image selection changed', {
          from: previousSelection,
          to: id,
          imageName: selectedImageData?.name,
          selectedRegion,
          isAdvancedRegion
        });
      }
    } catch (error) {
      handleOperationError(error, 'customImages');
      
      logUserAction('custom_image_selection_error', {
        attemptedSelection: id,
        previousSelection,
        errorType: error.name,
        isAdvancedRegion
      });
    }
  };

  // Handle volume selection with validation and error handling
  const handleVolumeSelect = (id) => {
    const previousSelection = selectedVolume;
    
    try {
      if (id && typeof onSelectVolume === 'function') {
        // Only allow selection of bootable volumes
        const selectedVol = bootableVolumes.find(vol => vol.id === id);
        if (selectedVol) {
          onSelectVolume(id);
          setIsOpen(false);
          clearError();
          
          logUserAction('volume_selected', {
            previousSelection,
            newSelection: id,
            volumeName: selectedVol.name,
            volumeSize: selectedVol.size,
            selectedRegion,
            availableBootableVolumes: bootableVolumes.length,
            isAdvancedRegion
          });
          
          logInfo('Volume selection changed', {
            from: previousSelection,
            to: id,
            volumeName: selectedVol.name,
            volumeSize: selectedVol.size,
            selectedRegion,
            isAdvancedRegion
          });
          
          // If selected volume is bootable, deselect OS image
          if (typeof onSelectOS === 'function') {
            onSelectOS('');
            
            logUserAction('os_deselected_due_to_volume_selection', {
              selectedVolume: id,
              previousOS: selectedOS,
              isAdvancedRegion
            });
            
            logInfo('OS deselected due to volume selection', {
              selectedVolume: id,
              previousOS: selectedOS,
              isAdvancedRegion
            });
          }
        }
      }
    } catch (error) {
      handleOperationError(error, 'volumes');
      
      logUserAction('volume_selection_error', {
        attemptedSelection: id,
        previousSelection,
        errorType: error.name,
        isAdvancedRegion
      });
    }
  };

  // Handle attachable volume selection toggle (ADVANCED REGIONS ONLY)
 const handleAttachableVolumeToggle = (volumeId) => {
  if (!isAdvancedRegion) return;
  
  // Check if this volume is already selected as OS boot source
  if (selectedImageType === 'user_volume' && selectedVolumeData?.volume_id === volumeId) {
    return; // Prevent selection - volume already used for boot
  }
  
  try {
    setSelectedAttachableVolumes(prev => {
      const isSelected = prev.includes(volumeId);
      const newSelection = isSelected 
        ? prev.filter(id => id !== volumeId)
        : prev.length < 10 
          ? [...prev, volumeId]
          : prev;
      
      logUserAction('attachable_volume_toggled', {
        volumeId,
        wasSelected: isSelected,
        newSelectionCount: newSelection.length,
        maxReached: prev.length >= 10 && !isSelected
      });
      
      return newSelection;
    });
  } catch (error) {
    handleOperationError(error, 'attachableVolumes');
  }
};
  // Handle new volume creation (ADVANCED REGIONS ONLY)
  const handleCreateVolume = (volumeData) => {
    if (!isAdvancedRegion) return;
    
    try {
      if (newVolumes.length >= 10) {
        logUserAction('volume_creation_blocked', {
          reason: 'max_limit_reached',
          currentCount: newVolumes.length,
          maxAllowed: 10
        });
        return;
      }
      
      const newVolume = {
        id: `new-${Date.now()}`,
        name: volumeData.name,
        size: volumeData.size,
        type: volumeData.type,
        isNew: true
      };
      
      setNewVolumes(prev => [...prev, newVolume]);
      setShowVolumeModal(false);
      
      logUserAction('new_volume_created', {
        volumeName: volumeData.name,
        volumeSize: volumeData.size,
        volumeType: volumeData.type,
        totalNewVolumes: newVolumes.length + 1
      });
      
    } catch (error) {
      handleOperationError(error, 'volumeCreation');
    }
  };

  // Handle volume edit
  const handleEditVolume = (volumeId, updatedData) => {
    try {
      setNewVolumes(prev => prev.map(vol => 
        vol.id === volumeId 
          ? { ...vol, ...updatedData }
          : vol
      ));
      
      setShowEditModal(false);
      setEditingVolume(null);
      
      logUserAction('volume_edited', {
        volumeId,
        updatedData
      });
    } catch (error) {
      handleOperationError(error, 'volumeEdit');
    }
  };

  // Handle new volume removal (ADVANCED REGIONS ONLY)
  const handleRemoveNewVolume = (volumeId) => {
    if (!isAdvancedRegion) return;
    
    try {
      setNewVolumes(prev => prev.filter(vol => vol.id !== volumeId));
      
      logUserAction('new_volume_removed', {
        volumeId,
        remainingCount: newVolumes.length - 1
      });
    } catch (error) {
      handleOperationError(error, 'volumeRemoval');
    }
  };

  // Handle attach modal save
  const handleAttachModalSave = () => {
    try {
      setShowAttachModal(false);
      setAttachModalSearch('');
      
      logUserAction('attach_volumes_saved', {
        selectedCount: selectedAttachableVolumes.length,
        selectedVolumes: selectedAttachableVolumes
      });
      
      logInfo('Attach volumes saved', {
        selectedCount: selectedAttachableVolumes.length,
        selectedRegion,
        isAdvancedRegion
      });
    } catch (error) {
      handleOperationError(error, 'attachVolumeSave');
    }
  };

  // Handle boot source change with error handling
  const handleBootSourceChange = (sourceId) => {
    const previousSource = selectedSource;
    
    try {
      if (typeof onSelectSource === 'function') {
        onSelectSource(sourceId);
        clearError();
        
        logUserAction('boot_source_changed', {
          previousSource,
          newSource: sourceId,
          isAuthenticated,
          selectedRegion,
          isAdvancedRegion
        });
        
        logInfo('Boot source changed', {
          from: previousSource,
          to: sourceId,
          isAuthenticated,
          selectedRegion,
          isAdvancedRegion
        });
      }
    } catch (error) {
      handleOperationError(error, 'general');
      
      logUserAction('boot_source_change_error', {
        attemptedSource: sourceId,
        previousSource,
        errorType: error.name,
        isAdvancedRegion
      });
    }
  };

  // Handle dropdown toggle
  const handleDropdownToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    
    logUserAction('dropdown_toggled', {
      opened: newState,
      selectedSource,
      selectedRegion,
      isAdvancedRegion,
      hasContent: selectedSource === 'os_images' ? filteredVersions.length > 0 :
                   selectedSource === 'volumes' ? bootableVolumes.length > 0 :
                   selectedSource === 'custom_images' ? filteredCustomImages.length > 0 :
                   selectedSource === 'attachable' ? attachableVolumes.length > 0 : false
    });
  };

  // Handle OS volume size change (ADVANCED REGIONS ONLY)
  const handleOsVolumeSizeChange = (size) => {
    if (!isAdvancedRegion) return;
    
    const numericSize = Math.min(Math.max(parseInt(size) || 100, 100), 10000);
    setOsVolumeSize(numericSize);
    
    logUserAction('os_volume_size_changed', {
      newSize: numericSize,
      selectedRegion
    });
  };

  // Handle input change with improved validation
  const handleInputChange = (e) => {
    const value = e.target.value;
    
    // Extract numeric value only, removing everything that's not a number
    const numericValue = value.replace(/\D/g, '');
    
    if (numericValue === '') {
      setInputValue('GB');
      setIsInputValid(false);
      return;
    }
    
    // Always maintain "GB" suffix when focused
    const formattedValue = inputFocused ? `${numericValue} GB` : `${numericValue} GB`;
    setInputValue(formattedValue);
    
    const parsed = parseInt(numericValue);
    if (parsed >= 100 && parsed <= 10000) {
      setIsInputValid(true);
      setOsVolumeSize(parsed);
    } else {
      setIsInputValid(false);
    }
    
    // Maintain cursor position before "GB"
    setTimeout(() => {
      const numericPart = numericValue;
      if (e.target.selectionStart > numericPart.length) {
        e.target.setSelectionRange(numericPart.length, numericPart.length);
      }
    }, 0);
  };

  // Handle key press to prevent non-numeric input
  const handleInputKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, and arrow keys
    if ([8, 9, 27, 13, 37, 38, 39, 40, 46].indexOf(e.keyCode) !== -1 ||
        // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true)) {
      
      // Handle arrow up/down for increment/decrement
      if (e.keyCode === 38) { // Arrow up
        e.preventDefault();
        const newValue = Math.min(osVolumeSize + 100, 10000);
        handleOsVolumeSizeChange(newValue);
        setInputValue(`${newValue} GB`);
      } else if (e.keyCode === 40) { // Arrow down
        e.preventDefault();
        const newValue = Math.max(osVolumeSize - 100, 100);
        handleOsVolumeSizeChange(newValue);
        setInputValue(`${newValue} GB`);
      } else if (e.keyCode === 13 || e.keyCode === 9) { // Enter or Tab
        e.target.blur();
      }
      
      return;
    }
    
    // Ensure that it's a number and stop the keypress if not
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };

  const handleInputBlur = (e) => {
  setInputFocused(false);
  const value = e.target.value.replace(/\D/g, '');
  
  if (value === '') {
    setOsVolumeSize(100);
    setInputValue('100 GB');
    setIsInputValid(true);
    return;
  }
  
  const parsed = parseInt(value);
  let finalValue = parsed;
  
  if (parsed < 100) {
    finalValue = 100;
  } else if (parsed > 10000) {
    finalValue = 10000;
  }
  
  setOsVolumeSize(finalValue);
  setInputValue(`${finalValue} GB`);
  setIsInputValid(true);
  
  e.target.style.borderColor = '#444';
  e.target.style.background = '#1d1d1d';
  e.target.style.boxShadow = 'none';
};
  // Handle input focus
  const handleInputFocus = (e) => {
    setInputFocused(true);
    setInputValue(`${osVolumeSize} GB`);
    e.target.style.borderColor = '#e0eacc';
    e.target.style.background = '#252525';
    e.target.style.boxShadow = '0 0 0 3px rgba(224, 234, 204, 0.1)';
    
    // Set cursor position before "GB"
    setTimeout(() => {
      const numericPart = osVolumeSize.toString();
      e.target.setSelectionRange(numericPart.length, numericPart.length);
    }, 0);
  };

  // Retry operation function
  const retryOperation = (operation) => {
    logUserAction('retry_operation_initiated', {
      operation,
      selectedSource,
      selectedRegion,
      isAdvancedRegion,
      previousError: error
    });
    
    clearError();
    if (operation === 'volumes') {
      setLoadingStates(prev => ({ ...prev, volumes: true }));
      setBootableVolumes([]);
      setNonBootableVolumes([]);
      
      logInfo('Retrying volumes operation', {
        selectedRegion,
        totalVolumes: volumes.length,
        isAdvancedRegion
      });
    } else if (operation === 'customImages') {
      setLoadingStates(prev => ({ ...prev, customImages: true }));
      setFilteredCustomImages([]);
      
      logInfo('Retrying custom images operation', {
        selectedRegion,
        totalCustomImages: customImages.length,
        isAdvancedRegion
      });
    } else if (operation === 'osImages') {
      setLoadingStates(prev => ({ ...prev, osImages: true }));
      setFilteredVersions([]);
      
      logInfo('Retrying OS images operation', {
        selectedRegion,
        totalOsVersions: osVersions.length,
        isAdvancedRegion
      });
    } else if (operation === 'attachableVolumes') {
      fetchAttachableVolumes();
    }
  };

  // Handle navigation to volumes
  const handleNavigateToVolumes = () => {
    logUserAction('navigate_to_volumes_clicked', {
      selectedSource,
      selectedRegion,
      isAdvancedRegion,
      hasBootableVolumes: bootableVolumes.length > 0,
      hasNonBootableVolumes: nonBootableVolumes.length > 0,
      totalVolumes: volumes.length
    });
    
    logInfo('User navigating to volumes page', {
      selectedSource,
      selectedRegion,
      isAdvancedRegion,
      volumesContext: {
        bootable: bootableVolumes.length,
        nonBootable: nonBootableVolumes.length,
        total: volumes.length
      }
    });
    
    if (typeof onNavigateToVolumes === 'function') {
      onNavigateToVolumes();
    }
  };

  // Safely get the selected OS name for display
  const getSelectedOSName = () => {
    if (!Array.isArray(filteredVersions)) return '';
    
    const selected = filteredVersions.find(os => os && os.id === selectedOS);
    return selected && selected.name ? sanitizeText(selected.name) : '';
  };

  // Safely get the selected custom image name for display
  const getSelectedCustomImageName = () => {
    if (!Array.isArray(filteredCustomImages)) return '';
    
    const selected = filteredCustomImages.find(img => img && img.id === selectedCustomImage);
    return selected && selected.name ? sanitizeText(selected.name) : '';
  };

  // Safely get the selected volume name for display
// Safely get the selected volume name for display
const getSelectedVolumeName = () => {
  if (!Array.isArray(bootableVolumes)) return '';
  
  const selected = bootableVolumes.find(vol => vol && vol.id === selectedVolume);
  if (!selected) return '';
  
  return sanitizeText(selected.name);
};
  // Safe rendering of name, truncate if too long - responsive truncation
  const safeRenderName = (name, isMobile = false) => {
    if (!name) return '';
    const safeName = sanitizeText(name);
    const maxLength = isMobile ? 30 : 50; // Shorter on mobile
    return safeName.length > maxLength ? safeName.substring(0, maxLength - 3) + '...' : safeName;
  };

  // Handle keyboard accessibility for dropdown
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleDropdownToggle();
      e.preventDefault();
      
      logUserAction('keyboard_dropdown_toggle', {
        key: e.key,
        selectedSource,
        opened: !isOpen,
        isAdvancedRegion
      });
    } else if (e.key === 'Escape' && isOpen) {
      setIsOpen(false);
      
      logUserAction('keyboard_dropdown_close', {
        key: e.key,
        selectedSource,
        isAdvancedRegion
      });
    }
  };

  // Loading indicator component - mobile optimized
  const LoadingIndicator = ({ text = "Loading..." }) => (
    <div className="flex items-center justify-center py-6 sm:py-4 md:py-4">
      <div className="animate-spin rounded-full h-6 w-6 sm:h-5 sm:w-5 md:h-5 md:w-5 border-b-2 border-[#e0eacc] mr-3"></div>
      <span className="text-gray-300 text-base sm:text-sm md:text-sm">{text}</span>
    </div>
  );

  // Error display component with retry option - mobile optimized
  const ErrorDisplay = ({ message, onRetry, operation }) => (
    <div className="p-4 sm:p-4 md:p-3 bg-red-900 bg-opacity-20 border border-red-800 rounded-lg">
      <div className="flex items-start">
        <svg className="h-6 w-6 sm:h-5 sm:w-5 md:h-5 md:w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
        <div>
          <p className="text-red-400 text-base sm:text-sm md:text-sm">{message}</p>
          {onRetry && (
            <button
              onClick={() => onRetry(operation)}
              className="mt-3 text-sm bg-red-700 hover:bg-red-600 text-white px-4 py-2 rounded transition-colors min-h-[44px] sm:min-h-auto"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Volume creation modal (ADVANCED REGIONS ONLY)
  const VolumeCreationModal = () => {
    const [volumeName, setVolumeName] = useState('');
    const [volumeSize, setVolumeSize] = useState(100);
    const [volumeType, setVolumeType] = useState('NVMe');
    const [inputFocused, setInputFocused] = useState(false);
    
    const [inputValue, setInputValue] = useState('100 GB');
    const [isInputValid, setIsInputValid] = useState(true);

    const handleInputChange = (e) => {
      const value = e.target.value;
      const numericValue = value.replace(/\D/g, '');
      
      if (numericValue === '') {
        setInputValue('GB');
        setIsInputValid(false);
        return;
      }
      
      const formattedValue = `${numericValue} GB`;
      setInputValue(formattedValue);
      
      const parsed = parseInt(numericValue);
      if (parsed >= 100 && parsed <= 10000) {
        setIsInputValid(true);
        setVolumeSize(parsed);
      } else {
        setIsInputValid(false);
      }
      
      setTimeout(() => {
        const numericPart = numericValue;
        if (e.target.selectionStart > numericPart.length) {
          e.target.setSelectionRange(numericPart.length, numericPart.length);
        }
      }, 0);
    };

  const handleInputBlur = (e) => {
  setInputFocused(false);
  const value = e.target.value.replace(/\D/g, '');
  
  if (value === '') {
    setVolumeSize(100);
    setInputValue('100 GB');
    setIsInputValid(true);
    return;
  }
  
  const parsed = parseInt(value);
  let finalValue = parsed;
  
  if (parsed < 100) {
    finalValue = 100;
  } else if (volumeType === 'HDD' && parsed > 16000) {
    finalValue = 16000;
  } else if (volumeType === 'NVMe' && parsed > 10000) {
    finalValue = 10000;
  }
  
  setVolumeSize(finalValue);
  setInputValue(`${finalValue} GB`);
  setIsInputValid(true);
  
  e.target.style.borderColor = '#333';
  e.target.style.background = '#1d1d1d';
  e.target.style.boxShadow = 'none';
};
    const handleInputFocus = (e) => {
      setInputFocused(true);
      setInputValue(`${volumeSize} GB`);
      e.target.style.borderColor = '#e0eacc';
      e.target.style.background = '#252525';
      e.target.style.boxShadow = '0 0 0 3px rgba(224, 234, 204, 0.1)';
      
      setTimeout(() => {
        const numericPart = volumeSize.toString();
        e.target.setSelectionRange(numericPart.length, numericPart.length);
      }, 0);
    };

    const handleInputKeyDown = (e) => {
      if ([8, 9, 27, 13, 37, 38, 39, 40, 46].indexOf(e.keyCode) !== -1 ||
          (e.keyCode === 65 && e.ctrlKey === true) ||
          (e.keyCode === 67 && e.ctrlKey === true) ||
          (e.keyCode === 86 && e.ctrlKey === true) ||
          (e.keyCode === 88 && e.ctrlKey === true)) {
        
        if (e.keyCode === 38) {
          e.preventDefault();
          const newValue = Math.min(volumeSize + 100, 10000);
          setVolumeSize(newValue);
          setInputValue(`${newValue} GB`);
        } else if (e.keyCode === 40) {
          e.preventDefault();
          const newValue = Math.max(volumeSize - 100, 100);
          setVolumeSize(newValue);
          setInputValue(`${newValue} GB`);
        } else if (e.keyCode === 13 || e.keyCode === 9) {
          e.target.blur();
        }
        
        return;
      }
      
      if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
        e.preventDefault();
      }
    };

   const handleSubmit = () => {
  const validation = validateVolumeName(volumeName, newVolumes);
  if (!validation.valid) {
    setNameError(validation.error);
    return;
  }
  
  handleCreateVolume({
    name: volumeName.trim(),
    size: volumeSize,
    type: volumeType
  });
};
    const handleCancel = () => {
      setShowVolumeModal(false);
      setVolumeName('');
      setVolumeSize(100);
      setVolumeType('NVMe');
      setInputValue('100 GB');
      setIsInputValid(true);
      setInputFocused(false);
    };

    if (!showVolumeModal) return null;

    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: window.innerWidth < 768 ? '16px' : '0px'
      }}>
        <div style={{
          backgroundColor: '#121212',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '24px',
          width: '100%',
          maxWidth: window.innerWidth < 768 ? '100%' : '400px',
          maxHeight: window.innerWidth < 768 ? '90vh' : 'auto',
          overflowY: window.innerWidth < 768 ? 'auto' : 'visible',
          margin: window.innerWidth < 768 ? '0' : '16px'
        }}>
          <h3 style={{
            color: '#e0eacc',
            fontSize: '18px',
            fontWeight: 600,
            marginBottom: '16px'
          }}>Configure Volume</h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
  <label style={{
    display: 'block',
    color: 'white',
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '8px'
  }}>Name</label>
  <input
    type="text"
    id="volume-name"
    value={volumeName}
    onChange={(e) => {
      let newName = e.target.value;
      newName = newName.replace(/[^a-zA-Z0-9_-]/g, '');
      setVolumeName(newName);
      setNameError('');
    }}
    style={{
      width: '100%',
      backgroundColor: '#1d1d1d',
      border: `1px solid ${nameError ? '#ff8080' : '#333333'}`,
      borderRadius: '4px',
      padding: '8px 12px',
      color: 'white',
      fontSize: '14px',
      outline: 'none'
    }}
    onFocus={(e) => e.target.style.borderColor = '#e0eacc'}
    onBlur={(e) => e.target.style.borderColor = nameError ? '#ff8080' : '#333333'}
    placeholder="volume-name"
    minLength={3}
    maxLength={30}
    pattern="[a-zA-Z0-9_-]+"
    disabled={loadingStates?.creating || false}
    required
  />
  {nameError && (
    <div style={{
      color: '#ff8080',
      fontSize: '12px',
      marginTop: '4px'
    }}>
      {nameError}
    </div>
  )}
</div>
            
            <div>
              <label style={{
                display: 'block',
                color: 'white',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '8px'
              }}>Size (GB)</label>
              <input
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                onSelect={(e) => {
                  const numericPart = volumeSize.toString();
                  if (e.target.selectionEnd > numericPart.length) {
                    e.target.setSelectionRange(
                      Math.min(e.target.selectionStart, numericPart.length),
                      numericPart.length
                    );
                  }
                }}
                onClick={(e) => {
                  if (inputFocused) {
                    const numericPart = volumeSize.toString();
                    setTimeout(() => {
                      if (e.target.selectionStart > numericPart.length) {
                        e.target.setSelectionRange(numericPart.length, numericPart.length);
                      }
                    }, 0);
                  }
                }}
                style={{
                  width: '100%',
                  backgroundColor: '#1d1d1d',
                  border: `2px solid ${isInputValid ? '#333' : '#ff4444'}`,
                  borderRadius: '4px',
                  padding: '8px 12px',
                  color: 'white',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!inputFocused) {
                    e.target.style.borderColor = isInputValid ? '#555' : '#ff4444';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!inputFocused) {
                    e.target.style.borderColor = isInputValid ? '#333' : '#ff4444';
                  }
                }}
              />
            </div>
            
            <div>
              <label style={{
                display: 'block',
                color: 'white',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '8px'
              }}>Type</label>
              <select
                value={volumeType}
                onChange={(e) => setVolumeType(e.target.value)}
                style={{
                  width: '100%',
                  backgroundColor: '#1d1d1d',
                  border: '1px solid #333',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  color: 'white',
                  fontSize: '14px',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#e0eacc'}
                onBlur={(e) => e.target.style.borderColor = '#333'}
              >
                <option value="NVMe">NVMe</option>
                <option value="HDD">HDD</option>
              </select>
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            marginTop: '24px'
          }}>
            <button
              onClick={handleCancel}
              style={{
                padding: '8px 16px',
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#444'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#333'}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!volumeName.trim()}
              style={{
                padding: '8px 16px',
                backgroundColor: volumeName.trim() ? '#e0eacc' : '#666',
                color: volumeName.trim() ? 'black' : '#999',
                border: 'none',
                borderRadius: '8px',
                cursor: volumeName.trim() ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                transition: 'background-color 0.2s',
                opacity: volumeName.trim() ? 1 : 0.5
              }}
              onMouseEnter={(e) => {
                if (volumeName.trim()) {
                  e.target.style.backgroundColor = '#c8d9a9';
                }
              }}
              onMouseLeave={(e) => {
                if (volumeName.trim()) {
                  e.target.style.backgroundColor = '#e0eacc';
                }
              }}
            >
              Add Volume
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Volume Management Modal Component
  const VolumeManagementModal = () => {
    if (!showManageModal) return null;

    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: window.innerWidth < 768 ? '16px' : '0px'
      }}>
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '8px',
          width: window.innerWidth < 768 ? '100%' : '600px',
          maxHeight: window.innerWidth < 768 ? '90vh' : '500px',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            padding: '20px 20px 16px 20px',
            borderBottom: '1px solid #333',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{
              color: '#e0eacc',
              fontSize: '18px',
              fontWeight: 600
            }}>Manage Volumes</div>
           <button
  onClick={() => setShowManageModal(false)}
  style={{
    background: 'none',
    border: 'none',
    color: '#999',
    fontSize: '20px',
    cursor: 'pointer',
    width: window.innerWidth < 768 ? '44px' : '30px',
    height: window.innerWidth < 768 ? '44px' : '30px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s'
  }}
  aria-label="Close manage volumes modal"
>
  ×
</button>
          </div>
          
          <div style={{
            padding: '20px',
            flex: 1,
            overflowY: 'auto'
          }}>
            {/* Create Section */}
            <div style={{
              marginBottom: '24px',
              paddingBottom: '20px',
              borderBottom: '1px solid #333'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px'
              }}>
                <div style={{
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 500
                }}>Create New Volume</div>
                <button
                  onClick={() => setShowVolumeModal(true)}
                  disabled={newVolumes.length >= 10}
                  style={{
                    backgroundColor: newVolumes.length >= 10 ? '#666' : '#e0eacc',
                    color: newVolumes.length >= 10 ? '#999' : 'black',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: newVolumes.length >= 10 ? 'not-allowed' : 'pointer',
                    opacity: newVolumes.length >= 10 ? 0.5 : 1
                  }}
                >
                  + Create
                </button>
              </div>
              <div style={{
                color: '#999',
                fontSize: '12px'
              }}>
                Add new volumes to your deployment ({newVolumes.length} of 10 used)
              </div>
            </div>
            
            {/* Existing Volumes */}
            {newVolumes.length > 0 && (
              <div>
                <div style={{
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 500,
                  marginBottom: '12px'
                }}>Existing Volumes</div>
                
                {newVolumes.map((volume) => (
                  <div key={volume.id} style={{
                    backgroundColor: '#121212',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    padding: '12px 16px',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.2s'
                  }}>
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: 500,
                        minWidth: '120px'
                      }}>{volume.name}</div>
                      <div style={{
                        color: '#999',
                        fontSize: '12px'
                      }}>{volume.type} • {volume.size}GB</div>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <button
                        onClick={() => {
                          setEditingVolume(volume);
                          setShowEditModal(true);
                        }}
                        style={{
                          background: 'none',
                          border: '1px solid #555',
                          color: '#ccc',
                          padding: '4px 8px',
                          borderRadius: '8px',
                          fontSize: '11px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleRemoveNewVolume(volume.id)}
                        style={{
                          background: 'none',
                          border: '1px solid #755',
                          color: '#f87171',
                          padding: '4px 8px',
                          borderRadius: '8px',
                          fontSize: '11px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div style={{
            padding: '16px 20px',
            borderTop: '1px solid #333',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{
              color: '#999',
              fontSize: '13px'
            }}>
              {newVolumes.length} volumes created • {newVolumes.reduce((total, vol) => total + vol.size, 0)}GB total
            </div>
            <button
              onClick={() => setShowManageModal(false)}
              style={{
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                padding: window.innerWidth < 768 ? '12px 20px' : '8px 20px',
                borderRadius: '8px',
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                minHeight: window.innerWidth < 768 ? '44px' : 'auto'
              }}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Volume Edit Modal Component
  const VolumeEditModal = () => {
    const [volumeName, setVolumeName] = useState(editingVolume?.name || '');
    const [volumeSize, setVolumeSize] = useState(editingVolume?.size || 100);
    const [volumeType, setVolumeType] = useState(editingVolume?.type || 'NVMe');
    const [inputFocused, setInputFocused] = useState(false);
    const [inputValue, setInputValue] = useState(`${editingVolume?.size || 100} GB`);
    const [isInputValid, setIsInputValid] = useState(true);

    const handleInputChange = (e) => {
      const value = e.target.value;
      const numericValue = value.replace(/\D/g, '');
      
      if (numericValue === '') {
        setInputValue('GB');
        setIsInputValid(false);
        return;
      }
      
      const formattedValue = `${numericValue} GB`;
      setInputValue(formattedValue);
      
      const parsed = parseInt(numericValue);
      if (parsed >= 100 && parsed <= 10000) {
        setIsInputValid(true);
        setVolumeSize(parsed);
      } else {
        setIsInputValid(false);
      }
      
      setTimeout(() => {
        const numericPart = numericValue;
        if (e.target.selectionStart > numericPart.length) {
          e.target.setSelectionRange(numericPart.length, numericPart.length);
        }
      }, 0);
    };

   const handleInputBlur = (e) => {
  setInputFocused(false);
  const value = e.target.value.replace(/\D/g, '');
  
  if (value === '') {
    setVolumeSize(100);
    setInputValue('100 GB');
    setIsInputValid(true);
    return;
  }
  
  const parsed = parseInt(value);
  let finalValue = parsed;
  
  if (parsed < 100) {
    finalValue = 100;
  } else if (volumeType === 'HDD' && parsed > 16000) {
    finalValue = 16000;
  } else if (volumeType === 'NVMe' && parsed > 10000) {
    finalValue = 10000;
  }
  
  setVolumeSize(finalValue);
  setInputValue(`${finalValue} GB`);
  setIsInputValid(true);
  
  e.target.style.borderColor = '#333';
  e.target.style.background = '#121212';
  e.target.style.boxShadow = 'none';
};

    const handleInputFocus = (e) => {
      setInputFocused(true);
      setInputValue(`${volumeSize} GB`);
      e.target.style.borderColor = '#e0eacc';
      e.target.style.background = '#1d1d1d';
      e.target.style.boxShadow = '0 0 0 3px rgba(224, 234, 204, 0.1)';
      
      setTimeout(() => {
        const numericPart = volumeSize.toString();
        e.target.setSelectionRange(numericPart.length, numericPart.length);
      }, 0);
    };

    const handleInputKeyDown = (e) => {
      if ([8, 9, 27, 13, 37, 38, 39, 40, 46].indexOf(e.keyCode) !== -1 ||
          (e.keyCode === 65 && e.ctrlKey === true) ||
          (e.keyCode === 67 && e.ctrlKey === true) ||
          (e.keyCode === 86 && e.ctrlKey === true) ||
          (e.keyCode === 88 && e.ctrlKey === true)) {
        
        if (e.keyCode === 38) {
          e.preventDefault();
          const newValue = Math.min(volumeSize + 100, 10000);
          setVolumeSize(newValue);
          setInputValue(`${newValue} GB`);
        } else if (e.keyCode === 40) {
          e.preventDefault();
          const newValue = Math.max(volumeSize - 100, 100);
          setVolumeSize(newValue);
          setInputValue(`${newValue} GB`);
        } else if (e.keyCode === 13 || e.keyCode === 9) {
          e.target.blur();
        }
        
        return;
      }
      
      if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
        e.preventDefault();
      }
    };

   const handleSubmit = () => {
  if (!editingVolume) return;
  
  const otherVolumes = newVolumes.filter(vol => vol.id !== editingVolume.id);
  const validation = validateVolumeName(volumeName, otherVolumes);
  if (!validation.valid) {
    setNameError(validation.error);
    return;
  }
handleEditVolume(editingVolume.id, {
    name: volumeName.trim(),
    size: volumeSize,
    type: volumeType
  });
};

    const handleCancel = () => {
      setShowEditModal(false);
      setEditingVolume(null);
    };

    if (!showEditModal || !editingVolume) return null;

    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: window.innerWidth < 768 ? '16px' : '0px'
      }}>
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '24px',
          width: '100%',
          maxWidth: window.innerWidth < 768 ? '100%' : '400px',
          maxHeight: window.innerWidth < 768 ? '90vh' : 'auto',
          overflowY: window.innerWidth < 768 ? 'auto' : 'visible',
          margin: window.innerWidth < 768 ? '0' : '16px'
        }}>
          <h3 style={{
            color: '#e0eacc',
            fontSize: '18px',
            fontWeight: 600,
            marginBottom: '16px'
          }}>Edit Volume - {editingVolume.name}</h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
              <label style={{
                display: 'block',
                color: 'white',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '8px'
              }}>Volume Name</label>
              <input
                type="text"
                id="edit-volume-name"
                value={volumeName}
             onChange={(e) => {
  let newName = e.target.value;
  newName = newName.replace(/[^a-zA-Z0-9_-]/g, '');
  setVolumeName(newName);
  setNameError('');
}}
                style={{
                  width: '100%',
                  backgroundColor: '#121212',
                  border: `1px solid ${nameError ? '#ff8080' : '#333333'}`,
                  borderRadius: '4px',
                  padding: '10px 12px',
                  color: 'white',
                  fontSize: '14px',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#e0eacc'}
                onBlur={(e) => e.target.style.borderColor = nameError ? '#ff8080' : '#333333'}
minLength={3}
maxLength={30}
pattern="[a-zA-Z0-9_-]+"
                disabled={loadingStates?.creating || false}
                required
              />
              {nameError && (
                <div style={{
                  color: '#ff8080',
                  fontSize: '12px',
                  marginTop: '4px'
                }}>
                  {nameError}
                </div>
              )}
            </div>
            
            <div>
              <label style={{
                display: 'block',
                color: 'white',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '8px'
              }}>Volume Type</label>
              <select
                value={volumeType}
                onChange={(e) => setVolumeType(e.target.value)}
                style={{
                  width: '100%',
                  backgroundColor: '#121212',
                  border: '1px solid #333',
                  borderRadius: '4px',
                  padding: '10px 12px',
                  color: 'white',
                  fontSize: '14px',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#e0eacc'}
                onBlur={(e) => e.target.style.borderColor = '#333'}
              >
                <option value="NVMe">NVMe (Fast)</option>
                <option value="HDD">HDD (Standard)</option>
              </select>
            </div>
            
            <div>
              <label style={{
                display: 'block',
                color: 'white',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '8px'
              }}>Volume Size</label>
              <input
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                onSelect={(e) => {
                  const numericPart = volumeSize.toString();
                  if (e.target.selectionEnd > numericPart.length) {
                    e.target.setSelectionRange(
                      Math.min(e.target.selectionStart, numericPart.length),
                      numericPart.length
                    );
                  }
                }}
                onClick={(e) => {
                  if (inputFocused) {
                    const numericPart = volumeSize.toString();
                    setTimeout(() => {
                      if (e.target.selectionStart > numericPart.length) {
                        e.target.setSelectionRange(numericPart.length, numericPart.length);
                      }
                    }, 0);
                  }
                }}
                style={{
                  width: '100%',
                  backgroundColor: '#121212',
                  border: `2px solid ${isInputValid ? '#333' : '#ff4444'}`,
                  borderRadius: '4px',
                  padding: '10px 12px',
                  color: 'white',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!inputFocused) {
                    e.target.style.borderColor = isInputValid ? '#555' : '#ff4444';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!inputFocused) {
                    e.target.style.borderColor = isInputValid ? '#333' : '#ff4444';
                  }
                }}
              />
              <div style={{
                color: '#999',
                fontSize: '12px',
                marginTop: '4px'
              }}>Minimum 100GB, Maximum 10000GB</div>
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            marginTop: '24px'
          }}>
            <button
              onClick={handleCancel}
              style={{
                padding: '8px 16px',
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'background-color 0.2s'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!volumeName.trim()}
              style={{
                padding: '8px 16px',
                backgroundColor: volumeName.trim() ? '#e0eacc' : '#666',
                color: volumeName.trim() ? 'black' : '#999',
                border: 'none',
                borderRadius: '8px',
                cursor: volumeName.trim() ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                transition: 'background-color 0.2s',
                opacity: volumeName.trim() ? 1 : 0.5
              }}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Attach Volumes Modal Component (NEW)
  const AttachVolumesModal = () => {
    // Use ref to maintain focus on search input
    const searchInputRef = useRef(null);
    
    // Use useMemo to prevent re-renders when filtering
    const filteredAttachableVolumes = useMemo(() => {
      return attachableVolumes.filter(volume =>
        volume.name.toLowerCase().includes(attachModalSearch.toLowerCase())
      );
    }, [attachableVolumes, attachModalSearch]);

    // Use useCallback to prevent input re-renders
    const handleSearchChange = useCallback((e) => {
      setAttachModalSearch(e.target.value);
    }, []);

    // Focus the input when modal opens
    useEffect(() => {
      if (showAttachModal && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [showAttachModal]);

    if (!showAttachModal) return null;

    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: window.innerWidth < 768 ? '16px' : '0px'
      }} onClick={(e) => {
        if (e.target === e.currentTarget) {
          setShowAttachModal(false);
        }
      }}>
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '12px',
          width: window.innerWidth < 768 ? '100%' : '600px',
          maxHeight: window.innerWidth < 768 ? '90vh' : '600px',
          display: 'flex',
          flexDirection: 'column',
          margin: window.innerWidth < 768 ? '0' : '20px'
        }}>
          {/* Modal Header */}
          <div style={{
            padding: '20px 20px 16px 20px',
            borderBottom: '1px solid #333',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{
              color: '#e0eacc',
              fontSize: window.innerWidth < 768 ? '16px' : '18px',
              fontWeight: 600
            }}>Select Volumes to Attach</div>
           <button
  onClick={() => setShowAttachModal(false)}
  style={{
    background: 'none',
    border: 'none',
    color: '#999',
    fontSize: '20px',
    cursor: 'pointer',
    width: window.innerWidth < 768 ? '44px' : '30px',
    height: window.innerWidth < 768 ? '44px' : '30px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s'
  }}
  aria-label="Close attach volumes modal"
  onMouseEnter={(e) => {
    e.target.style.background = '#333';
    e.target.style.color = 'white';
  }}
  onMouseLeave={(e) => {
    e.target.style.background = 'none';
    e.target.style.color = '#999';
  }}
>
  ×
</button>
          </div>
          
        {/* Modal Body */}
          <div style={{
            padding: '20px',
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Search Box */}
            <input 
              key="attach-volumes-search"
              ref={searchInputRef}
              type="text" 
              placeholder="Search volumes..."
              value={attachModalSearch}
              onChange={handleSearchChange}
              maxLength={30}
              style={{
                background: '#121212',
                border: '1px solid #333',
                borderRadius: '6px',
                padding: window.innerWidth < 768 ? '12px' : '10px 12px',
                color: 'white',
                width: '100%',
                marginBottom: '15px',
                fontSize: '14px',
                outline: 'none',
                minHeight: window.innerWidth < 768 ? '44px' : 'auto'
              }}
              onFocus={(e) => e.target.style.borderColor = '#e0eacc'}
              onBlur={(e) => e.target.style.borderColor = '#333'}
              autoComplete="off"
            />
            
            {/* Volumes List */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              maxHeight: '300px',
              overflowY: 'auto',
              flex: 1
            }}>
              {filteredAttachableVolumes.length > 0 ? filteredAttachableVolumes.map((volume) => {
                const isDisabledForBoot = selectedImageType === 'user_volume' && selectedVolumeData?.volume_id === volume.id;
                
                return (
                  <div
                    key={volume.id}
                    onClick={() => isDisabledForBoot ? null : handleAttachableVolumeToggle(volume.id)}
                    style={{
                      background: isDisabledForBoot ? '#0f0f0f' : '#121212',
                      border: `1px solid ${isDisabledForBoot ? '#555' : selectedAttachableVolumes.includes(volume.id) ? '#e0eacc' : '#333'}`,
                      borderRadius: '6px',
                      padding: window.innerWidth < 768 ? '16px' : '12px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: isDisabledForBoot ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      minHeight: window.innerWidth < 768 ? '60px' : 'auto',
                      opacity: isDisabledForBoot ? 0.5 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!isDisabledForBoot && !selectedAttachableVolumes.includes(volume.id)) {
                        e.target.style.borderColor = '#555';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isDisabledForBoot && !selectedAttachableVolumes.includes(volume.id)) {
                        e.target.style.borderColor = '#333';
                      }
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      flex: 1
                    }}>
                      <div style={{
                        width: window.innerWidth < 768 ? '24px' : '18px',
                        height: window.innerWidth < 768 ? '24px' : '18px',
                        border: `2px solid ${isDisabledForBoot ? '#555' : selectedAttachableVolumes.includes(volume.id) ? '#e0eacc' : '#555'}`,
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                        background: isDisabledForBoot ? 'transparent' : selectedAttachableVolumes.includes(volume.id) ? '#e0eacc' : 'transparent',
                        color: isDisabledForBoot ? '#555' : selectedAttachableVolumes.includes(volume.id) ? 'black' : 'transparent',
                        fontSize: window.innerWidth < 768 ? '14px' : '12px',
                        minWidth: window.innerWidth < 768 ? '44px' : 'auto',
                        minHeight: window.innerWidth < 768 ? '44px' : 'auto'
                      }}>
{isDisabledForBoot ? '' : selectedAttachableVolumes.includes(volume.id) && '✓'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          color: isDisabledForBoot ? '#777' : 'white',
                          fontSize: '14px',
                          fontWeight: 500,
                          marginBottom: '2px'
                        }}>
                          {volume.name}
                          {volume.bootable && (
                            <span style={{
                              color: isDisabledForBoot ? '#666' : '#e0eacc',
                              fontSize: '11px',
                              background: isDisabledForBoot ? 'rgba(102, 102, 102, 0.1)' : 'rgba(224, 234, 204, 0.1)',
                              padding: '2px 6px',
                              borderRadius: '3px',
                              marginLeft: '8px'
                            }}>
                              {isDisabledForBoot ? 'USED AS BOOT' : 'BOOTABLE'}
                            </span>
                          )}
                        </div>
                        <div style={{
                          color: isDisabledForBoot ? '#666' : '#999',
                          fontSize: '12px',
                          display: 'flex',
                          gap: '12px'
                        }}>
                          <span>{volume.size}GB</span>
                          <span>•</span>
                          <span>{volume.type || 'NVMe'}</span>
                          <span>•</span>
                          <span>Created {volume.created || '1 day ago'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }) : (
                <div style={{
                  textAlign: 'center',
                  color: '#999',
                  padding: '40px 20px',
                  fontSize: '14px'
                }}>
                  {attachModalSearch ? 'No volumes match your search' : 'No attachable volumes available'}
                </div>
              )}
            </div>
            
            {selectedAttachableVolumes.length >= 10 && (
              <div style={{
                color: '#ffa726',
                fontSize: '12px',
                marginTop: '8px',
                textAlign: 'center'
              }}>
                Maximum 10 volumes can be selected
              </div>
            )}
          </div>
          
          {/* Modal Footer */}
          <div style={{
            padding: '16px 20px',
            borderTop: '1px solid #333',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{
              color: '#999',
              fontSize: '13px'
            }}>
              {selectedAttachableVolumes.length === 0 
                ? 'No volumes selected' 
                : `${selectedAttachableVolumes.length}/${attachableVolumes.length} volumes selected • ${selectedAttachableVolumes.reduce((total, id) => {
                    const vol = attachableVolumes.find(v => v.id === id);
                    return total + (vol?.size || 0);
                  }, 0)}GB total`
              }
            </div>
            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <button
                onClick={() => setShowAttachModal(false)}
                style={{
                  background: '#333',
                  color: 'white',
                  border: 'none',
                  padding: window.innerWidth < 768 ? '12px 20px' : '8px 20px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  minHeight: window.innerWidth < 768 ? '44px' : 'auto'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAttachModalSave}
                style={{
                  background: '#e0eacc',
                  color: 'black',
                  border: 'none',
                  padding: window.innerWidth < 768 ? '12px 20px' : '8px 20px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  minHeight: window.innerWidth < 768 ? '44px' : 'auto'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Check if mobile for truncation
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

const renderOSImagesContent = () => {
    // Check authentication first
    if (!isAuthenticated) {
      return (
        <div className="relative w-full" ref={dropdownRef}>
          <div className="relative">
            <button
              type="button"
              disabled
              className="w-full flex items-center justify-between px-5 py-4 sm:px-4 sm:py-3 md:px-4 md:py-3 bg-[#1d1d1d] border border-[#333333] rounded-lg text-left text-white cursor-not-allowed shadow-sm min-h-[68px] sm:min-h-[60px] md:min-h-auto"
              style={{
                width: window.innerWidth >= 768 ? '125%' : '100%',
                marginLeft: window.innerWidth >= 768 ? '-13%' : '0%',
                opacity: 0.7
              }}
            >
              <div className="w-full overflow-hidden">
           <span className="block text-sm sm:text-xs md:text-xs font-light">
  Sign up to see OS Images
</span>
              </div>
              <svg 
                className="h-6 w-6 sm:h-5 sm:w-5 md:h-5 md:w-5 text-gray-400" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
      );
    }

    // Show loading state for OS images
    if (loadingStates.osImages || loadingStates.filtering) {
      return <LoadingIndicator text="Loading OS images..." />;
    }

    // Show error state with retry option
    if (error && selectedSource === 'os_images') {
      return (
        <ErrorDisplay 
          message={error} 
          onRetry={retryOperation} 
          operation="osImages" 
        />
      );
    }

    return (
      <div className="relative w-full" ref={dropdownRef}>
        <div className="relative">
          <button
            type="button"
            className="w-full flex items-center justify-between px-5 py-4 sm:px-4 sm:py-3 md:px-4 md:py-3 bg-[#1d1d1d] border border-[#333333] hover:border-[#e0eacc] rounded-lg text-left text-white focus:outline-none focus:ring-1 focus:ring-[#e0eacc] focus:border-[#e0eacc] transition-all duration-150 shadow-sm min-h-[68px] sm:min-h-[60px] md:min-h-auto"
            style={{
              width: window.innerWidth >= 768 ? '125%' : '100%',
              marginLeft: window.innerWidth >= 768 ? '-13%' : '0%'
            }}
            onClick={handleDropdownToggle}
            onKeyDown={handleKeyDown}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-labelledby="os-selection-label"
          >
            <div className="w-full overflow-hidden">
              <span id="os-selection-label" className="block text-base sm:text-sm md:text-sm font-medium truncate" title={getSelectedOSName()}>
                {safeRenderName(getSelectedOSName(), isMobile)}
              </span>
              <span className="block text-sm sm:text-xs md:text-xs text-gray-400">Select operating system</span>
              
              {/* Advanced region OS volume size configuration */}
              {isAdvancedRegion && (
                <div style={{
                  marginTop: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginLeft: '10px'
                  }}>
                    {/* Desktop slider - hidden on mobile */}
                    <div
                      className="hidden md:block"
                      style={{
                        width: '140.4px',
                        height: '4px',
                        background: selectedImageType === 'user_volume' ? '#222' : '#333333',
                        borderRadius: '2px',
                        position: 'relative',
                        cursor: selectedImageType === 'user_volume' ? 'not-allowed' : 'pointer',
                        marginLeft: '-2px',
                        transition: 'background 0.2s ease',
                        opacity: selectedImageType === 'user_volume' ? 0.5 : 1
                      }}
                      onMouseEnter={(e) => {
                        if (selectedImageType !== 'user_volume') {
                          e.currentTarget.style.background = '#444444';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedImageType !== 'user_volume') {
                          e.currentTarget.style.background = '#333333';
                        }
                      }}
                      onMouseDown={(e) => {
                        if (selectedImageType === 'user_volume') return;
                        
                        if (e.target === e.currentTarget) {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const clickX = e.clientX - rect.left;
                          
                          const percent = Math.max(0, Math.min(1, clickX / rect.width));
                          const newValue = Math.round(100 + percent * (10000 - 100));
                          handleOsVolumeSizeChange(newValue);
                          setInputValue(inputFocused ? `${newValue} GB` : `${newValue} GB`);
                          
                          e.preventDefault();
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          left: `${((osVolumeSize - 100) / (10000 - 100)) * 100}%`,
                          top: '-5px',
                          width: '14px',
                          height: '14px',
                          background: selectedImageType === 'user_volume' ? '#666' : '#e0eacc',
                          borderRadius: '50%',
                          cursor: selectedImageType === 'user_volume' ? 'not-allowed' : 'pointer',
                          transform: 'translateX(-50%)',
transition: 'background 0.2s ease, opacity 0.2s ease',
                          pointerEvents: 'none',
                          opacity: selectedImageType === 'user_volume' ? 0.5 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (selectedImageType !== 'user_volume') {
                            e.currentTarget.style.background = '#c8d9a9';
                            e.currentTarget.style.transform = 'translateX(-50%) scale(1.1)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedImageType !== 'user_volume') {
                            e.currentTarget.style.background = '#e0eacc';
                            e.currentTarget.style.transform = 'translateX(-50%) scale(1)';
                          }
                        }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          left: `${((osVolumeSize - 100) / (10000 - 100)) * 100}%`,
                          top: '-15px',
                          width: '34px',
                          height: '34px',
                          cursor: selectedImageType === 'user_volume' ? 'not-allowed' : 'pointer',
                          transform: 'translateX(-50%)',
                          zIndex: 1,
                        }}
                        onMouseDown={(e) => {
                          if (selectedImageType === 'user_volume') return;
                          
                          const trackRect = e.currentTarget.parentElement.getBoundingClientRect();
                          
                          const handleMouseMove = (moveEvent) => {
                            const newPercent = Math.max(0, Math.min(1, (moveEvent.clientX - trackRect.left) / trackRect.width));
                            const newVal = Math.round(100 + newPercent * (10000 - 100));
                            handleOsVolumeSizeChange(newVal);
                            setInputValue(inputFocused ? `${newVal} GB` : `${newVal} GB`);
                          };
                          
                          const handleMouseUp = () => {
                            document.removeEventListener('mousemove', handleMouseMove);
                            document.removeEventListener('mouseup', handleMouseUp);
                          };
                          
                          document.addEventListener('mousemove', handleMouseMove);
                          document.addEventListener('mouseup', handleMouseUp);
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      />
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginLeft: window.innerWidth >= 768 ? '25px' : '0px'
                    }}>
                      {/* Add debug logs HERE */}

                      <input
                        type="text"
                        value={inputValue}
                        onChange={selectedImageType === 'user_volume' ? () => {} : handleInputChange}
                        onFocus={selectedImageType === 'user_volume' ? () => {} : handleInputFocus}
                        onBlur={selectedImageType === 'user_volume' ? () => {} : handleInputBlur}
                        onKeyDown={selectedImageType === 'user_volume' ? () => {} : handleInputKeyDown}
                        onSelect={selectedImageType === 'user_volume' ? () => {} : (e) => {
                          const numericPart = osVolumeSize.toString();
                          if (e.target.selectionEnd > numericPart.length) {
                            e.target.setSelectionRange(
                              Math.min(e.target.selectionStart, numericPart.length),
                              numericPart.length
                            );
                          }
                        }}
                        onClick={selectedImageType === 'user_volume' ? () => {} : (e) => {
                          e.stopPropagation();
                          if (inputFocused) {
                            const numericPart = osVolumeSize.toString();
                            setTimeout(() => {
                              if (e.target.selectionStart > numericPart.length) {
                                e.target.setSelectionRange(numericPart.length, numericPart.length);
                              }
                            }, 0);
                          }
                        }}
                        readOnly={selectedImageType === 'user_volume'}
                        className="w-24 md:w-28"
                        style={{
                          minWidth: window.innerWidth < 768 ? '120px' : '100px',
                          maxWidth: window.innerWidth < 768 ? '120px' : '100px',
                          flexShrink: 0,
                          background: selectedImageType === 'user_volume' ? '#1a1a1a' : '#1d1d1d',
                          border: `2px solid ${isInputValid ? '#444' : '#ff4444'}`,
                          borderRadius: '2px',
                          textAlign: 'center',
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#ffffff',
                          padding: window.innerWidth < 768 ? '12px 6px' : '8px 6px',
                          outline: 'none',
                          transition: 'all 0.2s ease',
                          cursor: selectedImageType === 'user_volume' ? 'not-allowed' : 'text',
                          opacity: selectedImageType === 'user_volume' ? 0.7 : 1
                        }}
                        onMouseEnter={selectedImageType === 'user_volume' ? () => {} : (e) => {
                          if (!inputFocused) {
                            e.target.style.borderColor = isInputValid ? '#666' : '#ff4444';
                          }
                        }}
                        onMouseLeave={selectedImageType === 'user_volume' ? () => {} : (e) => {
                          if (!inputFocused) {
                            e.target.style.borderColor = isInputValid ? '#444' : '#ff4444';
                          }
                        }}
                      />
                      {/* Desktop increment buttons - hidden on mobile */}
                      <div className="hidden md:flex" style={{
                        flexDirection: 'column',
                        gap: '1px'
                      }}>
                        <button
                          type="button"
                          style={{
                            width: '18px',
                            height: '12px',
                            background: '#333',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '8px',
                            color: '#999',
                            transition: 'all 0.1s',
                            borderRadius: '2px 2px 0 0'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const newValue = Math.min(osVolumeSize + 100, 10000);
                            handleOsVolumeSizeChange(newValue);
                            setInputValue(inputFocused ? `${newValue} GB` : `${newValue} GB`);
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#555';
                            e.target.style.color = '#e0eacc';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#333';
                            e.target.style.color = '#999';
                          }}
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          style={{
                            width: '18px',
                            height: '12px',
                            background: '#333',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '8px',
                            color: '#999',
                            transition: 'all 0.1s',
                            borderRadius: '0 0 2px 2px'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const newValue = Math.max(osVolumeSize - 100, 100);
                            handleOsVolumeSizeChange(newValue);
                            setInputValue(inputFocused ? `${newValue} GB` : `${newValue} GB`);
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#555';
                            e.target.style.color = '#e0eacc';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#333';
                            e.target.style.color = '#999';
                          }}
                        >
                          ▼
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <svg 
              className={`h-6 w-6 sm:h-5 sm:w-5 md:h-5 md:w-5 text-gray-400 transform transition-transform duration-200 ease-in-out ${isOpen ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown menu */}
          {isOpen && (
            <div className="absolute z-20 mt-1 w-full rounded-lg bg-[#1d1d1d] shadow-lg ring-1 ring-black ring-opacity-5 border border-[#333333] transition-opacity duration-100 ease-in-out">
              <ul 
                className="py-1 max-h-60 overflow-auto custom-scrollbar" 
                role="listbox"
                tabIndex="-1"
                aria-labelledby="os-selection-label"
              >
                {Array.isArray(filteredVersions) && filteredVersions.length > 0 ? (
                  filteredVersions.map((os) => {
                    if (!os || !os.id) return null;
                    
                    return (
                      <li
                        key={os.id}
                        className={`
                          relative cursor-pointer select-none py-4 sm:py-3 md:py-2.5 pl-4 sm:pl-3 md:pl-3 pr-10 sm:pr-9 md:pr-9 text-base sm:text-sm md:text-sm min-h-[56px] sm:min-h-[48px] md:min-h-auto
                          ${selectedOS === os.id ? 'bg-[#252525] text-[#e0eacc]' : 'text-white hover:bg-[#252525] hover:text-[#e0eacc]'}
                        `}
                        role="option"
                        aria-selected={selectedOS === os.id}
                        onClick={() => handleSelect(os.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleSelect(os.id);
                            e.preventDefault();
                          }
                        }}
                        tabIndex="0"
                        title={os.name}
                      >
                        <div className="flex items-center">                        
                          <span className={`block truncate ${selectedOS === os.id ? 'font-medium' : 'font-normal'}`}>
                            {safeRenderName(os.name, isMobile)}
                          </span>
                        </div>

                        {selectedOS === os.id && (
                          <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#e0eacc]">
                            <svg className="h-6 w-6 sm:h-5 sm:w-5 md:h-5 md:w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </span>
                        )}
                      </li>
                    );
                  })
                ) : (
                  <li className="py-4 sm:py-3 md:py-2.5 pl-4 sm:pl-3 md:pl-3 pr-10 sm:pr-9 md:pr-9 text-base sm:text-sm md:text-sm text-gray-400 min-h-[56px] sm:min-h-[48px] md:min-h-auto flex items-center">
                    No OS images available for the selected region
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render volumes content based on selected source and region type - MODIFIED FOR CLEAN INTERFACE
  const renderVolumesContent = () => {
    // Show loading state
    if (loadingStates.volumes) {
      return <LoadingIndicator text="Loading volumes..." />;
    }

    // Show error state with retry option
    if (error && selectedSource === 'volumes') {
      return (
        <ErrorDisplay 
          message={error} 
          onRetry={retryOperation} 
          operation="volumes" 
        />
      );
    }

    if (isAdvancedRegion) {
      // NEW CLEAN INTERFACE WITH ADD + MANAGE BUTTONS
      return (
        <div className="mt-4 px-2">
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#1d1d1d',
            border: '1px solid #333',
            borderRadius: '8px',
            padding: '17px 16px'
          }}>
            <div style={{
              color: 'white',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>Volumes</span>
              <span style={{
                color: '#999',
                fontSize: '12px'
              }}>({newVolumes.length}/10)</span>
            </div>
            <div style={{
              display: 'flex',
              gap: '8px'
            }}>
              <button
                onClick={() => setShowVolumeModal(true)}
                disabled={newVolumes.length >= 10}
                style={{
                  backgroundColor: '#e0eacc',
                  color: 'black',
                  border: 'none',
                  padding: window.innerWidth < 768 ? '12px 16px' : '8px 16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: newVolumes.length >= 10 ? 'not-allowed' : 'pointer',
                  opacity: newVolumes.length >= 10 ? 0.5 : 1,
                  transition: 'all 0.2s',
                  minHeight: window.innerWidth < 768 ? '44px' : 'auto'
                }}
              >
                Add
              </button>
              <button
                onClick={() => setShowManageModal(true)}
                style={{
                  backgroundColor: '#333',
                  color: 'white',
                  border: 'none',
                  padding: window.innerWidth < 768 ? '12px 16px' : '8px 16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  minHeight: window.innerWidth < 768 ? '44px' : 'auto'
                }}
              >
                Manage
              </button>
            </div>
          </div>
        </div>
      );
    } else {
      // Legacy region volumes UI (EXACT CURRENT BEHAVIOR)
      // No volumes at all - show empty state with button
      if (bootableVolumes.length === 0 && nonBootableVolumes.length === 0) {
        return (
          <div className="mt-6 sm:mt-4 md:mt-4 p-6 sm:p-4 md:p-6 flex flex-col items-center justify-center">
            <p className="text-gray-300 text-center mb-4 sm:mb-3 md:mb-4 text-base sm:text-sm md:text-sm">
              No volumes available in this region
            </p>
            <button
              onClick={handleNavigateToVolumes}
              className="px-4 py-3 sm:py-2 md:py-1.5 text-base sm:text-sm md:text-sm bg-[#e0eacc] text-black rounded-lg font-medium transition-colors hover:bg-[#c8d9a9] focus:outline-none focus:ring-2 focus:ring-[#e0eacc] min-h-[48px] sm:min-h-[44px] md:min-h-auto"
            >
              Create volume
            </button>
          </div>
        );
      }

      // Has bootable volumes but no non-bootable volumes
      if (bootableVolumes.length > 0 && nonBootableVolumes.length === 0) {
        return (
          <div className="mt-4 sm:mt-3 md:mt-4 px-2 sm:px-1 md:px-2">
            <div className="mb-4 sm:mb-3 md:mb-3">
              <div className="text-base sm:text-sm md:text-sm text-gray-300 mb-3 sm:mb-2 md:mb-2">
                Select bootable volume:
              </div>
              <div className="relative w-full" ref={dropdownRef}>
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-5 py-4 sm:px-4 sm:py-3 md:px-4 md:py-2 bg-[#1d1d1d] border border-[#333333] hover:border-[#e0eacc] rounded-lg text-left text-white focus:outline-none focus:ring-1 focus:ring-[#e0eacc] focus:border-[#e0eacc] transition-all duration-150 shadow-sm min-h-[60px] sm:min-h-[52px] md:min-h-auto"
                  onClick={handleDropdownToggle}
                  onKeyDown={handleKeyDown}
                  aria-haspopup="listbox"
                  aria-expanded={isOpen}
                >
                  <span className="block text-base sm:text-sm md:text-sm truncate" title={selectedVolume ? getSelectedVolumeName() : ''}>
                    {selectedVolume ? safeRenderName(getSelectedVolumeName(), isMobile) : 'Select a volume'}
                  </span>
                  <svg 
                    className={`h-6 w-6 sm:h-5 sm:w-5 md:h-5 md:w-5 text-gray-400 transform transition-transform duration-200 ease-in-out ${isOpen ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isOpen && (
                  <div className="absolute z-10 mt-1 w-full rounded-lg bg-[#1d1d1d] shadow-lg ring-1 ring-black ring-opacity-5 border border-[#333333] transition-opacity duration-100 ease-in-out">
                    <ul 
                      className="py-1 max-h-60 overflow-auto custom-scrollbar" 
                      role="listbox"
                      tabIndex="-1"
                    >
                      {bootableVolumes.map((volume) => (
                        <li
                          key={volume.id}
                          className={`
                            relative cursor-pointer select-none py-4 sm:py-3 md:py-2 pl-4 sm:pl-3 md:pl-3 pr-10 sm:pr-9 md:pr-9 text-base sm:text-sm md:text-sm min-h-[56px] sm:min-h-[48px] md:min-h-auto
                            ${selectedVolume === volume.id ? 'bg-[#252525] text-[#e0eacc]' : 'text-white hover:bg-[#252525] hover:text-[#e0eacc]'}
                          `}
                          role="option"
                          aria-selected={selectedVolume === volume.id}
                          onClick={() => handleVolumeSelect(volume.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              handleVolumeSelect(volume.id);
                              e.preventDefault();
                            }
                          }}
                          tabIndex="0"
                        >
                  <div className="flex items-center justify-between">                        
  <span className={`block truncate ${selectedVolume === volume.id ? 'font-medium' : 'font-normal'}`}>
    {safeRenderName(volume.name, isMobile)}
  </span>
  <span className="text-sm sm:text-xs md:text-xs text-gray-400">
    {volume.size}GB
  </span>
</div>

                          {selectedVolume === volume.id && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#e0eacc]">
                              <svg className="h-6 w-6 sm:h-5 sm:w-5 md:h-5 md:w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <div className="text-center text-base sm:text-sm md:text-sm text-gray-400 mt-5 sm:mt-4 md:mt-4">
              No attachable volumes
            </div>
          </div>
        );
      }

      // Has bootable and non-bootable volumes or only non-bootable volumes
      return (
        <div className="mt-4 sm:mt-3 md:mt-4 px-2 sm:px-1 md:px-2">
          {/* Bootable volumes section */}
          {bootableVolumes.length > 0 ? (
            <div className="mb-5 sm:mb-4 md:mb-4">
              <div className="text-base sm:text-sm md:text-sm text-gray-300 mb-3 sm:mb-2 md:mb-2">
                Select bootable volume:
              </div>
              <div className="relative w-full" ref={dropdownRef}>
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-5 py-4 sm:px-4 sm:py-3 md:px-4 md:py-2 bg-[#1d1d1d] border border-[#333333] hover:border-[#e0eacc] rounded-lg text-left text-white focus:outline-none focus:ring-1 focus:ring-[#e0eacc] focus:border-[#e0eacc] transition-all duration-150 shadow-sm min-h-[60px] sm:min-h-[52px] md:min-h-auto"
                  onClick={handleDropdownToggle}
                  onKeyDown={handleKeyDown}
                  aria-haspopup="listbox"
                  aria-expanded={isOpen}
                >
                  <span className="block text-base sm:text-sm md:text-sm truncate">
                    {selectedVolume ? safeRenderName(getSelectedVolumeName(), isMobile) : 'Select a volume'}
                  </span>
                  <svg 
                    className={`h-6 w-6 sm:h-5 sm:w-5 md:h-5 md:w-5 text-gray-400 transform transition-transform duration-200 ease-in-out ${isOpen ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isOpen && (
                  <div className="absolute z-10 mt-1 w-full rounded-lg bg-[#1d1d1d] shadow-lg ring-1 ring-black ring-opacity-5 border border-[#333333] transition-opacity duration-100 ease-in-out">
                    <ul 
                      className="py-1 max-h-60 overflow-auto custom-scrollbar" 
                      role="listbox"
                      tabIndex="-1"
                    >
                      {bootableVolumes.map((volume) => (
                        <li
                          key={volume.id}
                          className={`
                            relative cursor-pointer select-none py-4 sm:py-3 md:py-2 pl-4 sm:pl-3 md:pl-3 pr-10 sm:pr-9 md:pr-9 text-base sm:text-sm md:text-sm min-h-[56px] sm:min-h-[48px] md:min-h-auto
                            ${selectedVolume === volume.id ? 'bg-[#252525] text-[#e0eacc]' : 'text-white hover:bg-[#252525] hover:text-[#e0eacc]'}
                          `}
                          role="option"
                          aria-selected={selectedVolume === volume.id}
                          onClick={() => handleVolumeSelect(volume.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              handleVolumeSelect(volume.id);
                              e.preventDefault();
                            }
                          }}
                          tabIndex="0"
                        >
             <div className="flex items-center justify-between">                        
  <span className={`block truncate ${selectedVolume === volume.id ? 'font-medium' : 'font-normal'}`}>
    {safeRenderName(volume.name, isMobile)}
  </span>
  <span className="text-sm sm:text-xs md:text-xs text-gray-400">
    {volume.size}GB
  </span>
</div>

                          {selectedVolume === volume.id && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#e0eacc]">
                              <svg className="h-6 w-6 sm:h-5 sm:w-5 md:h-5 md:w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="mb-5 sm:mb-4 md:mb-4">
              <div className="text-base sm:text-sm md:text-sm text-gray-300 mb-3 sm:mb-2 md:mb-2">
                No bootable volumes available
              </div>
              <button
                onClick={handleNavigateToVolumes}
                className="px-4 py-3 sm:py-2 md:py-1.5 text-base sm:text-sm md:text-sm bg-[#e0eacc] text-black rounded-lg font-medium transition-colors hover:bg-[#c8d9a9] focus:outline-none focus:ring-2 focus:ring-[#e0eacc] min-h-[48px] sm:min-h-[44px] md:min-h-auto"
              >
                Create bootable volume
              </button>
            </div>
          )}

 
        </div>
      );
    }
  };

  // Render attachable content (ADVANCED REGIONS ONLY)
  const renderAttachableContent = () => {
    if (!isAdvancedRegion) return null;

    if (loadingStates.attachableVolumes) {
      return <LoadingIndicator text="Loading attachable volumes..." />;
    }

  if (error && selectedSource === 'attachable') {
  return (
    <div className="mt-4 px-2">
      <div className="bg-red-900 bg-opacity-20 p-4 rounded-lg border border-red-800 text-red-200">
        <div className="flex items-start justify-between">
          <div className="flex items-start flex-1">
            <span className="text-red-400 mr-3 text-lg flex-shrink-0">⚠</span>
            <p className="text-sm leading-relaxed">{error}</p>
          </div>
          {showRetryAttachable && (
            <button
              onClick={retryFetchAttachableVolumes}
              className="ml-4 bg-[rgba(238,85,85,0.05)] border border-[#ff7777] text-[#ff7777] hover:bg-[rgba(238,85,85,0.1)] hover:border-[#ff5555] px-4 py-2 rounded-lg text-sm font-medium transition-all flex-shrink-0"
            >
              <span className="mr-2">↻</span>
              Retry
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

    return (
      <div className="mt-4 px-2">
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#1d1d1d',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '17px 16px'
        }}>
          <div style={{
            color: 'white',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>Attached Volumes</span>
            <span style={{
              color: '#999',
              fontSize: '12px'
            }}>({selectedAttachableVolumes.length}/{attachableVolumes.length} selected)</span>
          </div>
          <button
            onClick={() => {
              setAttachModalSearch(''); // Reset search when opening modal
              setShowAttachModal(true);
            }}
            style={{
              backgroundColor: '#333',
              color: 'white',
              border: 'none',
              padding: window.innerWidth < 768 ? '12px 16px' : '8px 16px',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s',
              minHeight: window.innerWidth < 768 ? '44px' : 'auto'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#444'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#333'}
          >
            Manage
          </button>
        </div>
      </div>
    );
  };

  // Render custom images content (LEGACY REGIONS ONLY)
  const renderCustomImagesContent = () => {
    if (isAdvancedRegion) return null;

    // Show loading state
    if (loadingStates.customImages) {
      return <LoadingIndicator text="Loading custom images..." />;
    }

    // Show error state with retry option
    if (error && selectedSource === 'custom_images') {
      return (
        <ErrorDisplay 
          message={error} 
          onRetry={retryOperation} 
          operation="customImages" 
        />
      );
    }

    if (!Array.isArray(filteredCustomImages) || filteredCustomImages.length === 0) {
      return (
        <div className="mt-6 sm:mt-4 md:mt-6 p-6 sm:p-4 md:p-6 flex flex-col items-center justify-center">
          <p className="text-gray-300 text-center mb-4 sm:mb-3 md:mb-4 text-base sm:text-sm md:text-sm">
            No custom images available in this region
          </p>
        </div>
      );
    }

    return (
      <div className="relative w-full" ref={dropdownRef}>
        <div className="relative">
          <button
            type="button"
            className="w-full flex items-center justify-between px-5 py-4 sm:px-4 sm:py-3 md:px-4 md:py-3 bg-[#1d1d1d] border border-[#333333] hover:border-[#e0eacc] rounded-lg text-left text-white focus:outline-none focus:ring-1 focus:ring-[#e0eacc] focus:border-[#e0eacc] transition-all duration-150 shadow-sm min-h-[68px] sm:min-h-[60px] md:min-h-auto"
            style={{
              width: window.innerWidth >= 768 ? '125%' : '100%',
              marginLeft: window.innerWidth >= 768 ? '-13%' : '0%'
            }}
            onClick={handleDropdownToggle}
            onKeyDown={handleKeyDown}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-labelledby="custom-image-selection-label"
          >
            <div className="w-full overflow-hidden">
              <span id="custom-image-selection-label" className="block text-base sm:text-sm md:text-sm font-medium truncate" title={getSelectedCustomImageName()}>
                {safeRenderName(getSelectedCustomImageName(), isMobile)}
              </span>
              <span className="block text-sm sm:text-xs md:text-xs text-gray-400">Select custom image</span>
            </div>
            <svg 
              className={`h-6 w-6 sm:h-5 sm:w-5 md:h-5 md:w-5 text-gray-400 transform transition-transform duration-200 ease-in-out ${isOpen ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown menu */}
          {isOpen && (
            <div className="absolute z-20 mt-1 w-full rounded-lg bg-[#1d1d1d] shadow-lg ring-1 ring-black ring-opacity-5 border border-[#333333] transition-opacity duration-100 ease-in-out">
              <ul 
                className="py-1 max-h-60 overflow-auto custom-scrollbar" 
                role="listbox"
                tabIndex="-1"
                aria-labelledby="custom-image-selection-label"
              >
                {filteredCustomImages.map((image) => {
                  if (!image || !image.id) return null;
                  
                  return (
                    <li
                      key={image.id}
                      className={`
                        relative cursor-pointer select-none py-4 sm:py-3 md:py-2.5 pl-4 sm:pl-3 md:pl-3 pr-10 sm:pr-9 md:pr-9 text-base sm:text-sm md:text-sm min-h-[56px] sm:min-h-[48px] md:min-h-auto
                        ${selectedCustomImage === image.id ? 'bg-[#252525] text-[#e0eacc]' : 'text-white hover:bg-[#252525] hover:text-[#e0eacc]'}
                      `}
                      role="option"
                      aria-selected={selectedCustomImage === image.id}
                      onClick={() => handleCustomImageSelect(image.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleCustomImageSelect(image.id);
                          e.preventDefault();
                        }
                      }}
                      tabIndex="0"
                      title={image.name}
                    >
                      <div className="flex items-center">                        
                        <span className={`block truncate ${selectedCustomImage === image.id ? 'font-medium' : 'font-normal'}`}>
                          {safeRenderName(image.name, isMobile)}
                        </span>
                      </div>

                      {selectedCustomImage === image.id && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#e0eacc]">
                          <svg className="h-6 w-6 sm:h-5 sm:w-5 md:h-5 md:w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Main component return
  return (
    <>
      <div className="bg-[#121212] p-5 sm:p-4 md:p-3 rounded-md mb-5 sm:mb-4 md:mb-2 w-full md:w-[90%] md:mx-auto">
        {/* Show general error if exists */}
        {error && !loadingStates.volumes && !loadingStates.customImages && !loadingStates.osImages && !loadingStates.attachableVolumes && (
          <div className="mb-5 sm:mb-4 md:mb-4">
            <ErrorDisplay 
              message={error} 
              onRetry={retryOperation} 
              operation="general" 
            />
          </div>
        )}

        {/* Tab navigation with proper validation - mobile optimized */}
        <div className="flex flex-wrap gap-3 sm:gap-2 md:gap-2 mb-5 sm:mb-4 md:mb-4">
          {availableSources.length > 0 ? (
            availableSources.map((source) => (
              <button
                key={source.id || 'unknown'}
                className={`
                  px-4 py-3 sm:px-3 sm:py-2 md:px-3 md:py-1.5
                  rounded-[0.5rem]
                  text-center
                  text-base sm:text-sm md:text-sm
                  transition-colors
                  inline-block
                  focus:outline-none focus:ring-2 focus:ring-[#e0eacc]
                  min-h-[52px] sm:min-h-[44px] md:min-h-auto
                  font-medium
                  ${selectedSource === source.id
                    ? 'bg-[#e0eacc] text-black shadow-sm'
                    : 'bg-[#1a1a1a] border border-[#333333] text-white hover:bg-[#242424] hover:border-[#444444]'
                  }
                `}
                onClick={() => handleBootSourceChange(source.id)}
                type="button"
                aria-pressed={selectedSource === source.id}
              >
                {safeRenderName(source.name || 'Unknown', isMobile)}
              </button>
            ))
          ) : (
            <div className="text-red-400 text-base sm:text-sm md:text-sm">No boot sources available</div>
          )}
        </div>

        {/* Show OS options when OS Images is selected */}
        {selectedSource === 'os_images' && renderOSImagesContent()}

        {/* Show volumes when Volumes is selected */}
        {selectedSource === 'volumes' && renderVolumesContent()}

        {/* Show custom images when Custom Images is selected (LEGACY REGIONS ONLY) */}
        {selectedSource === 'custom_images' && !isAdvancedRegion && renderCustomImagesContent()}

        {/* Show attachable volumes when Attachable is selected (ADVANCED REGIONS ONLY) */}
        {selectedSource === 'attachable' && isAdvancedRegion && renderAttachableContent()}
      </div>

      {/* Volume creation modal (ADVANCED REGIONS ONLY) */}
      {isAdvancedRegion && <VolumeCreationModal />}

      {/* Volume management modal (ADVANCED REGIONS ONLY) */}
      {isAdvancedRegion && <VolumeManagementModal />}

      {/* Volume edit modal (ADVANCED REGIONS ONLY) */}
      {isAdvancedRegion && <VolumeEditModal />}

      {/* Attach volumes modal (ADVANCED REGIONS ONLY) */}
      {isAdvancedRegion && <AttachVolumesModal />}
    </>
  );
};

BootSourceSelector.propTypes = {
  sources: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    })
  ),
  selectedSource: PropTypes.string,
  onSelectSource: PropTypes.func.isRequired,
  osVersions: PropTypes.array,
  selectedOS: PropTypes.string,
  onSelectOS: PropTypes.func.isRequired,
  selectedRegion: PropTypes.string,
  volumes: PropTypes.array,
  selectedVolume: PropTypes.string,
  onSelectVolume: PropTypes.func,
  onNavigateToVolumes: PropTypes.func,
  customImages: PropTypes.array,
  selectedCustomImage: PropTypes.string,
  onSelectCustomImage: PropTypes.func,
  onOsVolumeSizeChange: PropTypes.func,
  onNewVolumesChange: PropTypes.func,
  onAttachableVolumesChange: PropTypes.func
};

export default React.memo(BootSourceSelector);